import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LineChart from "../components/lineChart";
import * as dayjs from "dayjs";
import Modal from "bootstrap/js/dist/modal";
import toast from "react-hot-toast";
import { customToast } from "./../components/toasts";
import { useTheme } from "next-themes";
import { categoryButton, customPortfolioButton, tabButton, themeBasedPortfolioBtn } from "../components/customStyles";
import { amountToWords, format4, numberWithCommas, separateNumber } from "../utils/numbers";
import { createInvestorGoal, getInvestorGoals } from "../services/goals.service";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../store/AuthContext";
import { useTranslation } from "react-i18next";
import utc from "dayjs/plugin/utc";

import "./../styles/goals.css";
import { MultiRange, SingleRange } from "../components/multiRange";
import ProjectionTargetChart from "../components/goalProjectionChart";
import { BusinessIcon, CarIcon, EducationIcon, MarriageIcon, MobileIcon, OtherIcon, PropertyIcon, RetirementIcon, TravelIcon } from "../components/icons";
import GoalCards from "../components/goalCard";
import GoalCard from "../components/goalCard";
import Pagination from "../components/pagination";

dayjs.extend(utc);

const realMonthlyRate = (r, f) => Math.pow((1 + r) / (1 + f), 1 / 12) - 1;
const nominalMonthlyRate = (r) => Math.pow(1 + r, 1 / 12) - 1;

const safeNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const fmtOut = (v) => {
  if (typeof format4 === "function") return format4(Number(v));
  return Number(v).toFixed(4);
};

const pctToDecimal = (p) => (Number(p) || 0) / 100;

function i_nominal_monthly(rAnnual) {
  return Math.pow(1 + rAnnual, 1 / 12) - 1;
}

function i_real_monthly(rAnnual, fAnnual) {
  return Math.pow((1 + rAnnual) / (1 + fAnnual), 1 / 12) - 1;
}

function fv_lump(PV, i, N) {
  return Number(PV) * Math.pow(1 + i, N);
}

const goalIcons = [
  {
    title: "CAR",
    icon: (width, height) => <CarIcon width={width} height={height} />,
  },
  {
    title: "MOBILE",
    icon: (width, height) => <MobileIcon width={width} height={height} />,
  },
  {
    title: "PROPERTY",
    icon: (width, height) => <PropertyIcon width={width} height={height} />,
  },
  {
    title: "EDUCATION",
    icon: (width, height) => <EducationIcon width={width} height={height} />,
  },
  {
    title: "MARRIAGE",
    icon: (width, height) => <MarriageIcon width={width} height={height} />,
  },
  {
    title: "RETIREMENT",
    icon: (width, height) => <RetirementIcon width={width} height={height} />,
  },
  {
    title: "TRAVEL",
    icon: (width, height) => <TravelIcon width={width} height={height} />,
  },
  {
    title: "BUSINESS",
    icon: (width, height) => <BusinessIcon width={width} height={height} />,
  },
  {
    title: "OTHER",
    icon: (width, height) => <OtherIcon width={width} height={height} />,
  },
];

const Goals = ({ unitholder, portfolioFunds, setActiveSubMenu }) => {
  const [goals, setGoals] = useState([]);
  const [chartFlipView, setChartFlipView] = useState(null);
  const [otherOptions, setOtherOptions] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [goalData, setGoalData] = useState({
    portfolioProportion: 50,
    portfolioGrowthRate: 12,
    presentValue: "",
    inflationRate: 5,
    targetAge: [0, 10],
    target_amount: "",
    monthly_induction: "",
    category: "CAR",
    portfolio: { id: 1 },
    target_amount_nominal: "",
    monthly_induction_nominal: "",
    monthly_induction_real: "",
  });
  const [paginationData, setPaginationData] = useState(null);
  const [loadingGoals, setLoadingGoals] = useState(true);
  const [fetchingError, setFetchingError] = useState(false);
  const [activeGoal, setActiveGoal] = useState(null);
  const [activePageNumber, setActivePageNumber] = useState(1);

  const [monthlyGoalProjection, setMonthlyGoalProjection] = useState(null);
  const [screenSize, setScreenSize] = useState();

  const { theme } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [searchParams] = useSearchParams();
  // const searchTerm = searchParams.get("search");
  // const searchQuery = searchParams.get("query");
  const purchaseSearchModal = searchParams.get("modal");
  const purchaseSearchSymbol = searchParams.get("symbol");

  const hasHoldings = useMemo(() => {
    if (unitholder) {
      if (unitholder.balance_unit && unitholder.balance_unit !== "") {
        const holdings = JSON.parse(unitholder.balance_unit);

        if (holdings) {
          return holdings?.length !== 0;
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
    return false;
  }, [unitholder, portfolioFunds]);

  const modalProgressRef = useRef(null);
  const abortRef = useRef(null);

  const unitholderAge = useMemo(() => {
    if (!unitholder) return "0";

    if (!unitholder.dob) return "20";

    return dayjs().diff(dayjs(unitholder.dob, "YYYY-MM-DD"), "year");
  }, [unitholder]);

  const unitholderPortfolioSize = useMemo(() => {
    // return 1000000;
    let amount = 0;
    if (portfolioFunds && portfolioFunds.length !== 0) {
      portfolioFunds?.forEach((fund) => {
        const nav = parseFloat(fund.navData?.nav || 0);
        const units = parseFloat(fund.fund_unit);

        const calculations = nav * units;

        amount += calculations;
      });
    }

    setGoalData((prev) => ({ ...prev, presentValue: format4(parseFloat(amount) * (prev.portfolioProportion / 100)) }));

    return format4(amount);
  }, [unitholder, portfolioFunds]);

  const updateContribution = useMemo(() => {
    setGoalData((prev) => ({ ...prev, presentValue: format4((prev.portfolioProportion / 100) * unitholderPortfolioSize) }));
  }, [goalData.portfolioProportion]);

  const goalDuration = useMemo(() => {
    return goalData.targetAge[1] - goalData.targetAge[0];
  }, [goalData.targetAge]);

  const navigate = useNavigate();

  const modalRef = useRef(null);
  const goalModalRef = useRef(null);

  const projectionModalRef = useRef(null);
  const projectionModalInstanceRef = useRef(null);

  const goalDetailsRef = useRef(null);
  const goalDetailsInstanceRef = useRef(null);

  const closeAfterGoal = useRef(false);

  const lineChartOptions = {
    dataset: [
      {
        id: "dataset_raw",
        source: [
          // Header: Month, Investment, Type
          ["Month", "Investment", "Type"],
          ["Jan", 1000, "Current"],
          ["Jan", 1200, "Future"],
          ["Feb", 600, "Current"],
          ["Feb", 1400, "Future"],
          ["Mar", 900, "Current"],
          ["Mar", 1000, "Future"],
          ["Apr", 1100, "Current"],
          ["Apr", 1300, "Future"],
          ["May", 700, "Current"],
          ["May", 1500, "Future"],
          ["Jun", 1000, "Current"],
          ["Jun", 900, "Future"],
          ["Jul", 1000, "Current"],
          ["Jul", 800, "Future"],
          ["Aug", 100, "Current"],
          ["Aug", 2200, "Future"],
          ["Sep", 1400, "Current"],
          ["Sep", 800, "Future"],
          ["Oct", 1500, "Current"],
          ["Oct", 900, "Future"],
          ["Nov", 1600, "Current"],
          ["Nov", 1000, "Future"],
          ["Dec", 1700, "Current"],
          ["Dec", 1100, "Future"],
          // ... add additional months as needed
        ],
      },
      {
        id: "dataset_current",
        fromDatasetId: "dataset_raw",
        transform: {
          type: "filter",
          config: {
            dimension: "Type",
            "=": "Current",
          },
        },
      },
      {
        id: "dataset_future",
        fromDatasetId: "dataset_raw",
        transform: {
          type: "filter",
          config: {
            dimension: "Type",
            "=": "Future",
          },
        },
      },
    ],
    title: {
      text: "",
      show: false,
      padding: 10,
      left: 6,
      textStyle: {
        fontSize: 17,
        fontWeight: "600",
        color: "#5a5d5e",
      },
    },
    grid: {
      left: 20,
      right: 30,
      bottom: 30,
      // width: '100%',
      containLabel: true,
    },
    tooltip: {
      trigger: "axis",
    },
    xAxis: {
      type: "category",
      nameLocation: "middle",
      axisLabel: {
        color: theme === "light" ? "rgb(107, 111, 117)" : "rgb(240,240,240)",
      },
    },
    yAxis: {
      boundaryGap: 0,
      axisLabel: {
        color: theme === "light" ? "rgb(107, 111, 117)" : "rgb(240,240,240)",
      },
      splitLine: {
        lineStyle: {
          color: theme === "light" ? "rgb(200, 209, 243)" : "rgb(126, 133, 141)",
        },
      },
      // name: 'Investment'
    },
    series: [
      {
        type: "line",
        name: "Current",
        datasetId: "dataset_current",
        showSymbol: false,
        lineStyle: {
          color: "rgb(203, 110, 209)",
        },
        encode: {
          x: "Month",
          y: "Investment",
          itemName: "Month",
          tooltip: ["Investment"],
        },
      },
      {
        type: "line",
        name: "Required",
        datasetId: "dataset_future",
        showSymbol: false,
        lineStyle: {
          color: "rgb(55, 131, 223)",
        },
        encode: {
          x: "Month",
          y: "Investment",
          itemName: "Month",
          tooltip: ["Investment"],
        },
      },
    ],
  };

  const updateGoals = (data) => {
    setGoals(data);
  };

  function getPortfolioSize() {
    let amount = 0;
    portfolioFunds?.forEach((fund) => {
      const nav = parseFloat(fund.navData?.nav || 0);
      const units = parseFloat(fund.fund_unit);

      const calculations = nav * units;

      amount += calculations;
    });

    return amount;
  }

  function getPortfolioPortion(portfolioPortion) {
    let amount = 0;
    portfolioFunds?.forEach((fund) => {
      const nav = parseFloat(fund.navData?.nav || 0);
      const units = parseFloat(fund.fund_unit);

      const calculations = nav * units;

      amount += calculations;
    });

    return format4(amount * portfolioPortion);
  }

  // const handleContributionChange = (e) => {
  //   const { name, value } = e.target;

  //   const decimalLimit = name === "total_amount" ? 4 : 4;
  //   let formatted = "";

  //   const integerLimit = 12;

  //   if (value !== "") {
  //     let hasDecimal = false;
  //     let decimalCount = 0;
  //     let integerCount = 0;

  //     for (const x of value) {
  //       if (x === ".") {
  //         // allow only the first dot
  //         if (!hasDecimal) {
  //           formatted += x;
  //           hasDecimal = true;
  //         }
  //       } else if (x >= "0" && x <= "9") {
  //         if (hasDecimal) {
  //           // after the dot, only up to decimalLimit digits
  //           if (decimalCount < decimalLimit) {
  //             formatted += x;
  //             decimalCount += 1;
  //           }
  //         } else {
  //           // before the dot, limit integer digits
  //           if (integerCount < integerLimit) {
  //             formatted += x;
  //             integerCount++;
  //           }
  //         }
  //       } else if (x === ",") {
  //         // keep commas for now (you strip them below)
  //         formatted += x;
  //       }
  //       // everything else is skipped
  //     }
  //   }

  //   // remove commas for internal calculations
  //   formatted = formatted.split(",").join("");

  //   const intValue = parseFloat(formatted);

  //   const portfolioPct = formatted.length !== 0 ? Math.floor((intValue / 1000000) * 100) : '';

  //   setGoalData(prev => ({ ...prev, portfolioProportion: portfolioPct, presentValue: formatted }))
  // }
  function computeMonthlyProjection(startDate) {
    // test new formula
    const result = scheduleForPMTNominalConstant();

    // const r = Number(goalData.portfolioGrowthRate) / 100 || 0;
    // const f = Number(goalData.inflationRate) / 100 || 0;
    // const portion = Number(goalData.portfolioProportion) / 100 || 0;
    // const N = Math.round(parseFloat(goalDuration) * 12);
    // const PV = (parseFloat(unitholderPortfolioSize) || 0) * (portion || 0);

    // const i_real = realMonthlyRate(r, f);
    // const i_nom = nominalMonthlyRate(r);

    // const PMT_real = parseFloat(goalData.monthly_induction);
    // const PMT_nominal_reference = parseFloat(goalData.monthly_induction);
    // const target_nominal = parseFloat(goalData.target_amount_nominal);
    // const PV_future_real = PV * Math.pow(1 + i_real, N);
    // // const target_real = parseFloat(goalData.target_amount);
    // // const growthRatePct = r;
    // // const portionPct = portion;
    // // const portfolioSize = parseFloat(unitholderPortfolioSize);
    // const years = parseFloat(goalDuration);
    // // const inflationRatePct = f;

    // // parse DOB into a dayjs object once and validate
    // let dob = null;
    // if (unitholder.dob) {
    //   const parsedDob = dayjs(unitholder.dob);
    //   if (parsedDob.isValid()) {
    //     dob = parsedDob;
    //   } else {
    //     dob = null; // invalid DOB -> we won't compute ages
    //   }
    // } else {
    //   dob = dayjs().subtract(20, "year"); // invalid DOB -> we won't compute ages
    // }

    // // Helper: compute integer age at a given date (d)
    // // Accepts dayjs or string/date; will convert to dayjs internally.
    // const ageAtDate = (d) => {
    //   if (!dob) return null; // no DOB provided
    //   const D = dayjs(d); // IMPORTANT: convert here
    //   if (!D.isValid()) return null;
    //   // whole-year difference
    //   let yearsDiff = D.year() - dob.year();
    //   // birthday date in the year of D
    //   const birthdayThisYear = dob.year(D.year());
    //   // if the date D is before that year's birthday, subtract one
    //   if (D.isBefore(birthdayThisYear, "day")) yearsDiff -= 1;
    //   return yearsDiff;
    // };

    // const start = startDate ? dayjs(startDate) : dayjs();

    // // Build schedule
    // const schedule = [];
    // let balance_real = PV; // starting balance at t=0 (today)
    // let cum_real_contrib = 0;
    // let prevAge = dob ? ageAtDate(start) : null;

    // for (let m = 1; m <= N; m++) {
    //   // keep date as dayjs for calculations
    //   const dateObj = start.add(m, "month"); // dayjs instance

    //   // grow existing balance by real monthly rate
    //   balance_real = balance_real * (1 + i_real);

    //   // add contribution at month-end (ordinary annuity)
    //   const payment_real = PMT_real;
    //   balance_real = balance_real + payment_real;

    //   cum_real_contrib += payment_real;

    //   // nominal equivalents at month m (t = m months from now)
    //   const nominal_factor = Math.pow(1 + f, m / 12); // (1+f)^(m/12)
    //   const balance_nominal_end = balance_real * nominal_factor;
    //   const payment_nominal_equivalent = payment_real * nominal_factor;
    //   const cum_nominal_contrib = cum_real_contrib * nominal_factor; // approx nominal equivalent of cumulative real contributions

    //   // date for this month (optional)
    //   const date = start.add(m, "month").format("YYYY-MM-DD");

    //   // compute age at this date (ageAtDate now receives a dayjs instance)
    //   let age = null;
    //   let ageChanged = false;
    //   let ageBirthdayDate = null;
    //   if (dob) {
    //     age = ageAtDate(dateObj); // safe: ageAtDate converts internally and returns integer
    //     ageChanged = prevAge === null ? false : age > prevAge;
    //     if (ageChanged) {
    //       // birthday date (string) for UI
    //       ageBirthdayDate = dob.year(dateObj.year()).format("YYYY-MM-DD");
    //     }
    //     prevAge = age;
    //   }

    //   // compute age for this date
    //   // let age = null;
    //   // let ageChanged = false;
    //   // let ageBirthdayDate = null;
    //   // if (dob) {
    //   //   age = ageAtDate(date);
    //   //   ageChanged = prevAge === null ? false : age > prevAge;
    //   //   if (ageChanged) {
    //   //     // birthday date for the new age (the DOB shifted to this year)
    //   //     ageBirthdayDate = dob.year(date.year()).format("YYYY-MM-DD");
    //   //   }
    //   //   prevAge = age;
    //   // }

    //   schedule.push({
    //     month: m,
    //     date,
    //     payment_real: Number(payment_real.toFixed(4)),
    //     payment_nominal_equivalent: Number(payment_nominal_equivalent.toFixed(4)),
    //     balance_real_end: Number(balance_real.toFixed(4)),
    //     balance_nominal_end: Number(balance_nominal_end.toFixed(4)),
    //     cum_real_contrib: Number(cum_real_contrib.toFixed(4)),
    //     cum_nominal_contrib: Number(cum_nominal_contrib.toFixed(4)),
    //     age,
    //     ageChanged,
    //     ageBirthdayDate,
    //   });
    // }

    // const resultSummary = {
    //   PMT_real: Number(PMT_real.toFixed(4)),
    //   PMT_nominal_reference: Number(PMT_nominal_reference.toFixed(4)),
    //   target_nominal: Number(target_nominal.toFixed(4)),
    //   PV,
    //   PV_future_real: Number(PV_future_real.toFixed(4)),
    //   years,
    //   months: N,
    // };

    // const xData = schedule.map((row) => row.date);
    const xData = result.map((row) => row.date);

    // PROJECTION series should be balance_real_end (real)
    const projectionSeries = result.map((row) => (row && typeof row.balance_real_end === "number" ? row.balance_real_end : null));

    // TARGET series should be balance_nominal_end (nominal)
    const targetSeries = result.map((row) => (row && typeof row.balance_nominal_end === "number" ? row.balance_nominal_end : null));

    setChartData({ xData, projection: projectionSeries, target: targetSeries });
    setMonthlyGoalProjection(result);

    return { result };
  }

  function computeActiveGoalMonthlyProjection(goal) {
    const r = goal?.portfolio_growth_rate || 0;
    const f = goal?.annual_inflation_rate || 0;
    const portion = goal?.portfolio_portion || 0;
    const N = Math.round(parseFloat(goal?.goal_duration) * 12);
    const PV = goal?.portfolio_amount || 0;

    const i_real = realMonthlyRate(r, f);
    const i_nom = nominalMonthlyRate(r);

    const PMT_real = parseFloat(goal?.monthly_induction_real);
    const PMT_nominal_constant = parseFloat(goal?.monthly_induction_nominal);
    const target_nominal = parseFloat(goal?.target_amount_nominal);
    const PV_future_real = PV * Math.pow(1 + i_real, N);
    // const target_real = parseFloat(goalData.target_amount);
    // const growthRatePct = r;
    // const portionPct = portion;
    // const portfolioSize = parseFloat(unitholderPortfolioSize);
    const years = parseFloat(goal?.goal_duration);

    const schedule = [];
    let balance_real = Number(PV);
    // const start = dayjs(startDate);

    let dob = null;
    if (unitholder.dob) {
      const parsedDob = dayjs(unitholder.dob);
      if (parsedDob.isValid()) {
        dob = parsedDob;
      } else {
        dob = null; // invalid DOB -> we won't compute ages
      }
    } else {
      dob = dayjs().subtract(20, "year"); // invalid DOB -> we won't compute ages
    }

    const ageAtDate = (d) => {
      if (!dob) return null; // no DOB provided
      const D = dayjs(d); // IMPORTANT: convert here
      if (!D.isValid()) return null;
      // whole-year difference
      let yearsDiff = D.year() - dob.year();
      // birthday date in the year of D
      const birthdayThisYear = dob.year(D.year());
      // if the date D is before that year's birthday, subtract one
      if (D.isBefore(birthdayThisYear, "day")) yearsDiff -= 1;
      return yearsDiff;
    };

    // const start = startDate ? dayjs(startDate) : dayjs();
    const start = dayjs(goal?.target_date).subtract(goal?.goal_duration, "year");

    let prevAge = dob ? ageAtDate(start) : null;

    for (let m = 1; m <= N; m++) {
      // for nominal constant payment, the real-equivalent payment this month is:
      const nominal_factor = Math.pow(1 + f, m / 12);
      const payment_nominal = PMT_nominal_constant;
      const payment_real_equivalent = payment_nominal / nominal_factor; // convert current nominal payment to todays rupees
      // But we simulate growth in real terms:
      balance_real = balance_real * (1 + i_real);
      balance_real += payment_real_equivalent;

      const balance_nominal_end = balance_real * nominal_factor;

      const dateObj = start.add(m, "month"); // dayjs instance

      // date for this month (optional)
      const date = start.add(m, "month").format("YYYY-MM-DD");

      // compute age at this date (ageAtDate now receives a dayjs instance)
      let age = null;
      let ageChanged = false;
      let ageBirthdayDate = null;
      if (dob) {
        age = ageAtDate(dateObj); // safe: ageAtDate converts internally and returns integer
        ageChanged = prevAge === null ? false : age > prevAge;
        if (ageChanged) {
          // birthday date (string) for UI
          ageBirthdayDate = dob.year(dateObj.year()).format("YYYY-MM-DD");
        }
        prevAge = age;
      }

      schedule.push({
        month: m,
        date,
        // date: start.add(m, "month").format("YYYY-MM-DD"),
        payment_nominal_equivalent: Number(payment_nominal),
        payment_real: Number(payment_real_equivalent.toFixed(4)),
        balance_real_end: Number(balance_real.toFixed(4)),
        balance_nominal_end: Number(balance_nominal_end.toFixed(4)),
        age,
        ageChanged,
        ageBirthdayDate,
      });
    }

    const resultSummary = {
      PMT_real: Number(PMT_real.toFixed(4)),
      PMT_nominal_constant: Number(PMT_nominal_constant.toFixed(4)),
      target_nominal: Number(target_nominal.toFixed(4)),
      PV,
      PV_future_real: Number(PV_future_real.toFixed(4)),
      years,
      months: N,
    };

    const xData = schedule.map((row) => row.date);

    // PROJECTION series should be balance_real_end (real)
    const projectionSeries = schedule.map((row) => (row && typeof row.balance_real_end === "number" ? row.balance_real_end : null));

    // TARGET series should be balance_nominal_end (nominal)
    const targetSeries = schedule.map((row) => (row && typeof row.balance_nominal_end === "number" ? row.balance_nominal_end : null));

    setChartData({ xData, projection: projectionSeries, target: targetSeries });
    setMonthlyGoalProjection(schedule);

    return { summary: resultSummary, schedule };
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const decimalLimit = name === "total_amount" ? 4 : 4;
    let formatted = "";

    const integerLimit = 12;

    if (value !== "") {
      let hasDecimal = false;
      let decimalCount = 0;
      let integerCount = 0;

      for (const x of value) {
        if (x === ".") {
          // allow only the first dot
          if (!hasDecimal) {
            formatted += x;
            hasDecimal = true;
          }
        } else if (x >= "0" && x <= "9") {
          if (hasDecimal) {
            // after the dot, only up to decimalLimit digits
            if (decimalCount < decimalLimit) {
              formatted += x;
              decimalCount += 1;
            }
          } else {
            // before the dot, limit integer digits
            if (integerCount < integerLimit) {
              formatted += x;
              integerCount++;
            }
          }
        } else if (x === ",") {
          // keep commas for now (you strip them below)
          formatted += x;
        }
        // everything else is skipped
      }
    }

    // remove commas for internal calculations
    formatted = formatted.split(",").join("");

    // update raw field so UI shows what user typed
    setGoalData((prev) => ({ ...prev, [name]: formatted }));

    // Now update logic for each field
    if (name === "monthly_induction") {
      const parsed = parseFloat(formatted);

      if (!isNaN(parsed)) {
        const result = computeFromPMTNominalConstant(parsed);

        setGoalData((prev) => ({
          ...prev,
          monthly_induction: formatted, // keep user input visible
          target_amount: fmtOut(result.target_real), // real future purchasing-power required
          target_amount_nominal: fmtOut(result?.target_nominal || ""),
          // target_amount: fmtOut(FV_total_real), // real future purchasing-power required
          // target_amount_nominal: fmtOut(targetNominal),
          monthly_induction_nominal: fmtOut(result.PMT_nominal_constant),
        }));
      } else {
        // cleared input
        setGoalData((prev) => ({
          ...prev,
          monthly_induction: formatted,
          target_amount: "",
          target_amount_nominal: "",
          monthly_induction_nominal: "",
        }));
      }
    } else if (name === "target_amount") {
      const parsed = parseFloat(formatted);

      if (!isNaN(parsed)) {
        const result = computeFromTargetReal(parsed);

        setGoalData((prev) => ({
          ...prev,
          target_amount: formatted, // keep user-entered real visible
          monthly_induction: fmtOut(result.PMT_nominal_constant),
          monthly_induction_nominal: fmtOut(result.PMT_nominal_constant),
          monthly_induction_real: fmtOut(result.PMT_real),
          target_amount_nominal: fmtOut(result?.target_nominal || ""),
        }));
      } else {
        setGoalData((prev) => ({ ...prev, monthly_induction: "", monthly_induction_nominal: "", target_amount_nominal: "", target_amount: "" }));
      }
    }
  };

  const validateGoalInput = () => {
    const errors = {};

    for (const key in goalData) {
      if (!goalData[key]) {
        errors[key] = true;
      }
    }

    if (Object.keys(errors).length) {
      return false;
    }

    return true;
  };

  const handlePortfolioPortionChange = (newPortionPct) => {
    const PV = (parseFloat(unitholderPortfolioSize) || 0) * (pctToDecimal(newPortionPct) || 0);
    const years = goalDuration;

    const target_real = parseFloat(goalData?.target_amount);
    const r = pctToDecimal(goalData?.portfolioGrowthRate);
    const f = pctToDecimal(goalData?.inflationRate);
    const N = Math.round(Number(years) * 12);
    if (N <= 0) return { PMT_real: 0, PMT_nominal_constant: 0, target_nominal: 0, N };

    const i_real = i_real_monthly(r, f);
    const i_nom = i_nominal_monthly(r);

    const PV_future_real = fv_lump(PV, i_real, N);

    let PMT_real = 0;
    if (PV_future_real < Number(target_real) - 1e-9) {
      if (Math.abs(i_real) < 1e-12) {
        PMT_real = (Number(target_real) - PV_future_real) / N;
      } else {
        const powr = Math.pow(1 + i_real, N);
        PMT_real = ((Number(target_real) - PV_future_real) * i_real) / (powr - 1);
      }
      if (!isFinite(PMT_real) || PMT_real < 0) PMT_real = 0;
    }

    // nominal target and constant nominal PMT
    const target_nominal = Number(target_real) * Math.pow(1 + f, years);

    const PV_future_nominal = fv_lump(PV, i_nom, N);
    let PMT_nominal_constant = 0;
    if (PV_future_nominal < Number(target_nominal) - 1e-9) {
      if (Math.abs(i_nom) < 1e-12) {
        PMT_nominal_constant = (Number(target_nominal) - PV_future_nominal) / N;
      } else {
        const pown = Math.pow(1 + i_nom, N);
        PMT_nominal_constant = ((Number(target_nominal) - PV_future_nominal) * i_nom) / (pown - 1);
      }
      if (!isFinite(PMT_nominal_constant) || PMT_nominal_constant < 0) PMT_nominal_constant = 0;
    }

    setGoalData((prev) => ({
      ...prev,
      portfolioProportion: Number(newPortionPct) || 0,
      monthly_induction: fmtOut(PMT_nominal_constant),
      monthly_induction_nominal: fmtOut(PMT_nominal_constant), // constant nominal PMT reference
      monthly_induction_real: fmtOut(PMT_real), // constant nominal PMT reference
      target_amount_nominal: fmtOut(target_nominal || ""),
    }));
  };

  const handleGoalDurationChange = (newGoalDuration) => {
    const [lower, upper] = newGoalDuration;
    const newDuration = upper - lower;

    const PV = (parseFloat(unitholderPortfolioSize) || 0) * (pctToDecimal(goalData?.portfolioProportion) || 0);
    const years = newDuration;

    const target_real = parseFloat(goalData?.target_amount);
    const r = pctToDecimal(goalData?.portfolioGrowthRate);
    const f = pctToDecimal(goalData?.inflationRate);
    const N = Math.round(Number(years) * 12);
    if (N <= 0) return { PMT_real: 0, PMT_nominal_constant: 0, target_nominal: 0, N };

    const i_real = i_real_monthly(r, f);
    const i_nom = i_nominal_monthly(r);

    const PV_future_real = fv_lump(PV, i_real, N);

    let PMT_real = 0;
    if (PV_future_real < Number(target_real) - 1e-9) {
      if (Math.abs(i_real) < 1e-12) {
        PMT_real = (Number(target_real) - PV_future_real) / N;
      } else {
        const powr = Math.pow(1 + i_real, N);
        PMT_real = ((Number(target_real) - PV_future_real) * i_real) / (powr - 1);
      }
      if (!isFinite(PMT_real) || PMT_real < 0) PMT_real = 0;
    }

    // nominal target and constant nominal PMT
    const target_nominal = Number(target_real) * Math.pow(1 + f, years);

    const PV_future_nominal = fv_lump(PV, i_nom, N);
    let PMT_nominal_constant = 0;
    if (PV_future_nominal < Number(target_nominal) - 1e-9) {
      if (Math.abs(i_nom) < 1e-12) {
        PMT_nominal_constant = (Number(target_nominal) - PV_future_nominal) / N;
      } else {
        const pown = Math.pow(1 + i_nom, N);
        PMT_nominal_constant = ((Number(target_nominal) - PV_future_nominal) * i_nom) / (pown - 1);
      }
      if (!isFinite(PMT_nominal_constant) || PMT_nominal_constant < 0) PMT_nominal_constant = 0;
    }

    setGoalData((prev) => ({
      ...prev,
      targetAge: newGoalDuration, // store percent
      monthly_induction: fmtOut(PMT_nominal_constant), // real monthly induction (today's rupees)
      monthly_induction_nominal: fmtOut(PMT_nominal_constant), // constant nominal PMT reference
      monthly_induction_real: fmtOut(PMT_real), // constant nominal PMT reference
      target_amount_nominal: fmtOut(target_nominal || ""),
    }));
  };

  const handleOptionsChange = (e) => {
    const { name, value } = e.target;

    const integerLimit = 3;
    let formatted = "";

    if (value !== "") {
      let integerCount = 0;

      for (const x of value) {
        if (x === "." || x === "-") {
          continue;
        } else if (x >= "0" && x <= "9") {
          if (integerCount < integerLimit) {
            formatted += x;
            integerCount++;
          }
        } else if (x === ",") {
          formatted += x;
        }
      }
    }

    const formattedNoCommas = formatted.split(",").join("");

    const originalNumeric = Number(value.split(",").join(""));
    if (!Number.isNaN(originalNumeric) && originalNumeric < 0) {
      formatted = "0";
    }

    // Determine years (prefer targetAge array, then goalDurationYears, then target_date)
    let years = goalData.targetAge[1] - goalData.targetAge[0];

    if (!years || !isFinite(years) || years <= 0) {
      // invalid duration -> just update portion and return
      // setGoalData((prev) => ({ ...prev, targetAge: newGoalDuration }));
      return;
    }

    const N = Math.round(years * 12);
    if (N <= 0) {
      // setGoalData((prev) => ({ ...prev, targetAge: newGoalDuration }));
      return;
    }

    // rates convert percent -> decimal
    const r = (safeNum(e.target.name === "growth_rate" ? formatted : goalData.portfolioGrowthRate) || 0) / 100;
    const f = (safeNum(e.target.name === "inflation_rate" ? formatted : goalData.inflationRate) || 0) / 100;

    const i_real = realMonthlyRate(r, f);
    const i_nom = nominalMonthlyRate(r);

    const PV = (Number(unitholderPortfolioSize) || 0) * (parseFloat(goalData.portfolioProportion) / 100);

    // User-entered target_amount is REAL (today's rupees)
    const target_real = safeNum(goalData.target_amount);

    const PV_future_real = fv_lump(PV, i_real, N);

    let PMT_real = 0;
    if (PV_future_real < Number(target_real) - 1e-9) {
      if (Math.abs(i_real) < 1e-12) {
        PMT_real = (Number(target_real) - PV_future_real) / N;
      } else {
        const powr = Math.pow(1 + i_real, N);
        PMT_real = ((Number(target_real) - PV_future_real) * i_real) / (powr - 1);
      }
      if (!isFinite(PMT_real) || PMT_real < 0) PMT_real = 0;
    }

    // nominal target and constant nominal PMT
    const target_nominal = Number(target_real) * Math.pow(1 + f, years);

    const PV_future_nominal = fv_lump(PV, i_nom, N);
    let PMT_nominal_constant = 0;
    if (PV_future_nominal < Number(target_nominal) - 1e-9) {
      if (Math.abs(i_nom) < 1e-12) {
        PMT_nominal_constant = (Number(target_nominal) - PV_future_nominal) / N;
      } else {
        const pown = Math.pow(1 + i_nom, N);
        PMT_nominal_constant = ((Number(target_nominal) - PV_future_nominal) * i_nom) / (pown - 1);
      }
      if (!isFinite(PMT_nominal_constant) || PMT_nominal_constant < 0) PMT_nominal_constant = 0;
    }

    setGoalData((prev) => ({
      ...prev,
      inflationRate: e.target.name === "inflation_rate" ? formatted : prev.inflationRate, // store percent
      portfolioGrowthRate: e.target.name === "growth_rate" ? formatted : prev.portfolioGrowthRate, // store percent
      monthly_induction: fmtOut(PMT_nominal_constant), // real monthly induction (today's rupees)
      monthly_induction_nominal: fmtOut(PMT_nominal_constant), // constant nominal PMT reference
      monthly_induction_real: fmtOut(PMT_real), // constant nominal PMT reference
      target_amount_nominal: fmtOut(target_nominal || ""),
    }));
  };

  // const toggleOptions = () => {
  //   setOtherOptions((prev) => {
  //     if (prev) {
  //       setGoalData((goal) => ({
  //         ...goal,
  //         inflationRate: 5,
  //         portfolioGrowthRate: 12,
  //       }));
  //       handleOptionsDisable(5, 12);
  //       return false;
  //     } else {
  //       handleOptionsDisable(goalData.inflationRate, goalData.portfolioGrowthRate);
  //       return true;
  //     }
  //   });
  // };

  const handleAddGoal = (e) => {
    e.preventDefault();

    // if (!validateGoalInput()) return;

    if (!validateGoalInput()) {
      toast((t) => customToast("Missing Fields Data", "error", t), {
        duration: 3000,
        removeDelay: 1000,
        style: {
          padding: "0.75rem",
          margin: "0",
          minWidth: "200px",
          backgroundColor: "rgb(255,255,255)",
        },
      });

      return;
    }

    const createGoal = async () => {
      try {
        const response = await setGoal(goalData);

        const data = await response.json();

        toast((t) => customToast("Added Goal", "success", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            minWidth: "200px",
            backgroundColor: "rgb(255,255,255)",
          },
        });

        toggleGoalModal();
      } catch (error) {
        toast((t) => customToast("Error Adding Goal", "error", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            minWidth: "200px",
            backgroundColor: "rgb(255,255,255)",
          },
        });
      }
    };

    const handleAddThenFetch = async () => {
      try {
        await createGoal();
        await getGoals(updateGoals);
      } catch (error) {}
    };

    handleAddThenFetch();
  };

  const handleCardFlip = (cardIndex) => {
    if (chartFlipView === cardIndex) {
      setChartFlipView(null);
    } else {
      setChartFlipView(cardIndex);
    }
  };

  const getGoalsOnLoad = async () => {
    try {
      setLoadingGoals(true);

      const apiArgs = {
        email: user?.data?.email,
        folioNo: user?.data?.folio_no || user?.data?.main_folio_no,
        pageNumber: 1,
      };

      const response = await getInvestorGoals({ ...apiArgs });

      if (!response.error) {
        setGoals(response.data?.docs);
      } else {
        setFetchingError(true);
      }
    } catch (e) {
    } finally {
      setLoadingGoals(false);
    }
  };

  const handleGoalSelect = (goal) => {
    setActiveGoal(goal);

    computeActiveGoalMonthlyProjection(goal);

    if (goalDetailsRef.current) {
      goalDetailsInstanceRef.current?.toggle();
    }
  };

  const computeFromTargetReal = (amount) => {
    const PV = (parseFloat(unitholderPortfolioSize) || 0) * (pctToDecimal(goalData?.portfolioProportion) || 0);
    const years = goalDuration;

    const target_real = parseFloat(amount);
    const r = pctToDecimal(goalData?.portfolioGrowthRate);
    const f = pctToDecimal(goalData?.inflationRate);
    const N = Math.round(Number(years) * 12);
    if (N <= 0) return { PMT_real: 0, PMT_nominal_constant: 0, target_nominal: 0, N };

    const i_real = i_real_monthly(r, f);
    const i_nom = i_nominal_monthly(r);

    const PV_future_real = fv_lump(PV, i_real, N);

    let PMT_real = 0;
    if (PV_future_real < Number(target_real) - 1e-9) {
      if (Math.abs(i_real) < 1e-12) {
        PMT_real = (Number(target_real) - PV_future_real) / N;
      } else {
        const powr = Math.pow(1 + i_real, N);
        PMT_real = ((Number(target_real) - PV_future_real) * i_real) / (powr - 1);
      }
      if (!isFinite(PMT_real) || PMT_real < 0) PMT_real = 0;
    }

    // nominal target and constant nominal PMT
    const target_nominal = Number(target_real) * Math.pow(1 + f, years);

    const PV_future_nominal = fv_lump(PV, i_nom, N);
    let PMT_nominal_constant = 0;
    if (PV_future_nominal < Number(target_nominal) - 1e-9) {
      if (Math.abs(i_nom) < 1e-12) {
        PMT_nominal_constant = (Number(target_nominal) - PV_future_nominal) / N;
      } else {
        const pown = Math.pow(1 + i_nom, N);
        PMT_nominal_constant = ((Number(target_nominal) - PV_future_nominal) * i_nom) / (pown - 1);
      }
      if (!isFinite(PMT_nominal_constant) || PMT_nominal_constant < 0) PMT_nominal_constant = 0;
    }

    return {
      PMT_real,
      target_real,
      target_nominal,
      PMT_nominal_constant,
      i_real,
      i_nom,
      PV_future_real,
      PV_future_nominal: PV_future_nominal,
      N,
    };
  };

  const computeFromPMTReal = ({ PMT_real }) => {
    const pctToDecimal = (p) => (Number(p) || 0) / 100;

    function i_nominal_monthly(rAnnual) {
      return Math.pow(1 + rAnnual, 1 / 12) - 1;
    }

    function i_real_monthly(rAnnual, fAnnual) {
      return Math.pow((1 + rAnnual) / (1 + fAnnual), 1 / 12) - 1;
    }

    function fv_lump(PV, i, N) {
      return Number(PV) * Math.pow(1 + i, N);
    }

    const PV = (parseFloat(unitholderPortfolioSize) || 0) * (pctToDecimal(goalData?.portfolioProportion) || 0);
    const years = goalDuration;

    const r = pctToDecimal(goalData?.portfolioGrowthRate);
    const f = pctToDecimal(goalData?.inflationRate);
    const N = Math.round(Number(years) * 12);
    if (N <= 0) return { target_real: 0, target_nominal: 0 };

    const i_real = i_real_monthly(r, f);
    const i_nom = i_nominal_monthly(r);

    const PV_future_real = fv_lump(PV, i_real, N);
    const FV_annuity_real = Math.abs(i_real) < 1e-12 ? PMT_real * N : PMT_real * ((Math.pow(1 + i_real, N) - 1) / i_real);

    const target_real = PV_future_real + FV_annuity_real;
    const target_nominal = target_real * Math.pow(1 + f, years);

    // optional: compute constant nominal PMT needed to reach target_nominal
    const PV_future_nominal = fv_lump(PV, i_nom, N);
    let PMT_nominal_constant = 0;
    if (PV_future_nominal < Number(target_nominal) - 1e-9) {
      if (Math.abs(i_nom) < 1e-12) {
        PMT_nominal_constant = (Number(target_nominal) - PV_future_nominal) / N;
      } else {
        const pown = Math.pow(1 + i_nom, N);
        PMT_nominal_constant = ((Number(target_nominal) - PV_future_nominal) * i_nom) / (pown - 1);
      }
    }

    return {
      target_real,
      target_nominal,
      FV_annuity_real,
      PV_future_real,
      PMT_nominal_constant,
      i_real,
      i_nom,
      N,
    };
  };

  const scheduleForPMTReal = ({ PV, PMT_real, years, inflationRatePct, growthRatePct, startDate = new Date() }) => {
    const r = pctToDecimal(growthRatePct);
    const f = pctToDecimal(inflationRatePct);
    const N = Math.round(Number(years) * 12);
    const i_real = i_real_monthly(r, f);

    const schedule = [];
    let balance_real = Number(PV);
    const start = dayjs(startDate);

    for (let m = 1; m <= N; m++) {
      balance_real = balance_real * (1 + i_real);
      const payment_real = PMT_real;
      balance_real += payment_real;

      const nominal_factor = Math.pow(1 + f, m / 12);
      const balance_nominal_end = balance_real * nominal_factor;
      schedule.push({
        month: m,
        date: start.add(m, "month").format("YYYY-MM-DD"),
        payment_real: Number(payment_real),
        payment_nominal: Number((payment_real * nominal_factor).toFixed(4)),
        balance_real_end: Number(balance_real.toFixed(4)),
        balance_nominal_end: Number(balance_nominal_end.toFixed(4)),
      });
    }

    return schedule;
  };

  const scheduleForPMTNominalConstant = () => {
    const PV = (parseFloat(unitholderPortfolioSize) || 0) * (pctToDecimal(goalData?.portfolioProportion) || 0);
    const years = goalDuration;

    const PMT_nominal_constant = parseFloat(goalData?.monthly_induction);

    const r = pctToDecimal(goalData?.portfolioGrowthRate);
    const f = pctToDecimal(goalData?.inflationRate);
    const N = Math.round(Number(years) * 12);
    const i_nom = i_nominal_monthly(r);
    const i_real = i_real_monthly(r, f);

    const schedule = [];
    let balance_real = Number(PV);
    // const start = dayjs(startDate);

    let dob = null;
    if (unitholder.dob) {
      const parsedDob = dayjs(unitholder.dob);
      if (parsedDob.isValid()) {
        dob = parsedDob;
      } else {
        dob = null; // invalid DOB -> we won't compute ages
      }
    } else {
      dob = dayjs().subtract(20, "year"); // invalid DOB -> we won't compute ages
    }

    const ageAtDate = (d) => {
      if (!dob) return null; // no DOB provided
      const D = dayjs(d); // IMPORTANT: convert here
      if (!D.isValid()) return null;
      // whole-year difference
      let yearsDiff = D.year() - dob.year();
      // birthday date in the year of D
      const birthdayThisYear = dob.year(D.year());
      // if the date D is before that year's birthday, subtract one
      if (D.isBefore(birthdayThisYear, "day")) yearsDiff -= 1;
      return yearsDiff;
    };

    // const start = startDate ? dayjs(startDate) : dayjs();
    const start = dayjs();

    let prevAge = dob ? ageAtDate(start) : null;

    for (let m = 1; m <= N; m++) {
      // for nominal constant payment, the real-equivalent payment this month is:
      const nominal_factor = Math.pow(1 + f, m / 12);
      const payment_nominal = PMT_nominal_constant;
      const payment_real_equivalent = payment_nominal / nominal_factor; // convert current nominal payment to todays rupees
      // But we simulate growth in real terms:
      balance_real = balance_real * (1 + i_real);
      balance_real += payment_real_equivalent;

      const balance_nominal_end = balance_real * nominal_factor;

      const dateObj = start.add(m, "month"); // dayjs instance

      // date for this month (optional)
      const date = start.add(m, "month").format("YYYY-MM-DD");

      // compute age at this date (ageAtDate now receives a dayjs instance)
      let age = null;
      let ageChanged = false;
      let ageBirthdayDate = null;
      if (dob) {
        age = ageAtDate(dateObj); // safe: ageAtDate converts internally and returns integer
        ageChanged = prevAge === null ? false : age > prevAge;
        if (ageChanged) {
          // birthday date (string) for UI
          ageBirthdayDate = dob.year(dateObj.year()).format("YYYY-MM-DD");
        }
        prevAge = age;
      }

      schedule.push({
        month: m,
        date,
        // date: start.add(m, "month").format("YYYY-MM-DD"),
        payment_nominal_equivalent: Number(payment_nominal),
        payment_real: Number(payment_real_equivalent.toFixed(4)),
        balance_real_end: Number(balance_real.toFixed(4)),
        balance_nominal_end: Number(balance_nominal_end.toFixed(4)),
        age,
        ageChanged,
        ageBirthdayDate,
      });
    }

    return schedule;
  };

  const computeFromPMTNominalConstant = (PMT_nominal_constant) => {
    const pctToDecimal = (p) => (Number(p) || 0) / 100;

    function i_nominal_monthly(rAnnual) {
      return Math.pow(1 + rAnnual, 1 / 12) - 1;
    }

    function i_real_monthly(rAnnual, fAnnual) {
      return Math.pow((1 + rAnnual) / (1 + fAnnual), 1 / 12) - 1;
    }

    function fv_lump(PV, i, N) {
      return Number(PV) * Math.pow(1 + i, N);
    }

    const PV = (parseFloat(unitholderPortfolioSize) || 0) * (pctToDecimal(goalData?.portfolioProportion) || 0);
    const years = goalDuration;

    const r = pctToDecimal(goalData?.portfolioGrowthRate);
    const f = pctToDecimal(goalData?.inflationRate);
    const N = Math.round(Number(years) * 12);
    if (N <= 0) return { target_nominal: 0, target_real: 0, PMT_real: 0 };

    const i_nom = i_nominal_monthly(r);
    const i_real = i_real_monthly(r, f);

    const PV_future_nominal = fv_lump(PV, i_nom, N);
    const pown = Math.pow(1 + i_nom, N);
    const FV_annuity_nominal = Math.abs(i_nom) < 1e-12 ? PMT_nominal_constant * N : PMT_nominal_constant * ((pown - 1) / i_nom);

    const target_nominal = PV_future_nominal + FV_annuity_nominal;
    const target_real = target_nominal / Math.pow(1 + f, years);

    // canonical PMT_real (solve using target_real)
    const PV_future_real = fv_lump(PV, i_real, N);
    let PMT_real = 0;
    if (PV_future_real < Number(target_real) - 1e-9) {
      if (Math.abs(i_real) < 1e-12) {
        PMT_real = (Number(target_real) - PV_future_real) / N;
      } else {
        const powr = Math.pow(1 + i_real, N);
        PMT_real = ((Number(target_real) - PV_future_real) * i_real) / (powr - 1);
      }
      if (!isFinite(PMT_real) || PMT_real < 0) PMT_real = 0;
    }

    return {
      target_nominal,
      target_real,
      PMT_nominal_constant,
      PMT_real,
      i_nom,
      i_real,
      PV_future_nominal,
      PV_future_real,
      N,
    };
  };

  useEffect(() => {
    const apiArgs = {
      email: user?.data?.email,
      folioNo: user?.data?.folio_no || user?.data?.main_folio_no,
      pageNumber: 1,
      pageSize: 12,
    };
    getInvestorGoals(apiArgs).then((res) => {
      if (!res.error) {
        const { docs, ...pageInfo } = res?.data;
        setGoals(docs || []);
        setPaginationData(pageInfo);
      } else {
        setFetchingError(true);
      }
    });
    if (!user) {
      toast((t) => customToast("Please sign in to access this feature.", "error", t), {
        duration: 3000,
        removeDelay: 1000,
        style: {
          padding: "0.75rem",
          margin: "0",
          width: "300px",
          maxWidth: "100%",
          // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
          // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
          color: "rgb(33,37,41)",
          backgroundColor: "rgb(221, 204, 225)",
        },
      });

      navigate("/auth/login");

      return;
      // } else if (!user?.documentsVerified) {
      toast((t) => customToast("Please verify your account to access this feature", "error", t), {
        duration: 3000,
        removeDelay: 1000,
        style: {
          padding: "0.75rem",
          margin: "0",
          width: "300px",
          maxWidth: "100%",
          // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
          // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
          color: "rgb(33,37,41)",
          backgroundColor: "rgb(221, 204, 225)",
        },
      });

      navigate("/auth/verify");
    }

    const resetGoalModal = () => {
      if (modalProgressRef.current !== "continue") {
        setGoalData((prev) => ({
          portfolioProportion: 50,
          portfolioGrowthRate: 12,
          presentValue: prev.presentValue,
          inflationRate: 5,
          targetAge: [unitholderAge ? parseFloat(unitholderAge) : 0, unitholderAge ? parseFloat(unitholderAge) + 5 : 10],
          target_amount: "",
          monthly_induction: "",
          category: "CAR",
          portfolio: { id: 1 },
          target_amount_nominal: "",
          monthly_induction_nominal: "",
          monthly_induction_real: "",
        }));
      }
    };

    const onProjectionModalClose = () => {
      modalProgressRef.current = null;

      if (!closeAfterGoal.current) {
        if (goalModalRef.current) {
          modalRef.current?.toggle();
        }
      } else {
        resetGoalModal();
        closeAfterGoal.current = false;
      }
    };

    const resizeHandler = () => {
      setScreenSize(window?.innerWidth);
    };

    resizeHandler();
    window.addEventListener("resize", resizeHandler);

    // getGoals(updateGoals);

    if (goalModalRef.current) {
      modalRef.current = new Modal(goalModalRef.current, { backdrop: true, keyboard: true });

      goalModalRef.current.addEventListener("hidden.bs.modal", resetGoalModal);
    }

    if (goalDetailsRef.current) {
      goalDetailsInstanceRef.current = new Modal(goalDetailsRef.current, { backdrop: true, keyboard: true });

      goalDetailsRef.current.addEventListener("hidden.bs.modal", () => {
        setActiveGoal(null);
      });
    }

    if (projectionModalRef.current) {
      projectionModalInstanceRef.current = new Modal(projectionModalRef.current, { backdrop: true, keyboard: true });

      projectionModalRef.current.addEventListener("hidden.bs.modal", onProjectionModalClose);
    }

    return () => {
      window.removeEventListener("resize", resizeHandler);
      // if (goalModalRef.current) {
      //   modalRef.current?.hide();
      //   modalRef.current?.dispose();
      // }
    };
  }, []);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const waitAndUpdate = async () => {
      setLoadingGoals(true);

      await new Promise((res) => setTimeout(() => res(), 500));

      try {
        const apiArgs = {
          email: user?.data?.email,
          folioNo: user?.data?.folio_no || user?.data?.main_folio_no,
          pageNumber: activePageNumber,
          pageSize: 12,
        };

        const response = await getInvestorGoals(apiArgs, controller.signal);

        if (!response.error) {
          const { docs, ...pageInfo } = response?.data;
          setGoals(docs || []);
          setPaginationData(pageInfo);
        } else {
        }
      } finally {
        setLoadingGoals(false);
      }
    };

    if (user) {
      waitAndUpdate();
    }

    return () => {
      controller.abort();
    };
  }, [activePageNumber]);

  useEffect(() => {
    if (portfolioFunds && portfolioFunds.length !== 0 && purchaseSearchModal) {
      const currentFund = portfolioFunds.find((item) => item.fund_code === purchaseSearchSymbol);

      if (currentFund) {
        setActiveSubMenu('portfolio');
      }
    }
  }, [portfolioFunds, purchaseSearchModal]);

  const toggleGoalModal = () => {
    if (!hasHoldings) {
      toast((t) => customToast("You need to have a portfolio in order to create a goal.", "error", t), {
        duration: 300000,
        removeDelay: 1000,
        style: {
          padding: "0.75rem",
          margin: "0",
          width: "380px",
          maxWidth: "100%",
          // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
          // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
          color: "rgb(33,37,41)",
          backgroundColor: theme === "dark" ? "rgb(221, 204, 225)" : "rgb(221, 204, 225)",
        },
      });

      return;
    }
    modalRef.current.toggle();
    // modalRef.current.hide();
  };

  if (!user) {
    navigate("/auth/login");

    return <div></div>;
  }

  const fmtContribution = separateNumber(goalData.presentValue ? parseFloat(goalData.presentValue).toFixed(4) : "0", { format: true, digits: 4 });
  const fmtPayment = separateNumber(goalData?.monthly_induction || "0", { format: true, digits: 4 });
  const fmtTargetNominal = goalData?.target_amount_nominal ? separateNumber(goalData?.target_amount_nominal || "0", { format: true, digits: 4 }) : separateNumber("0");
  const fmtFinalBalance = separateNumber(
    monthlyGoalProjection?.[monthlyGoalProjection?.length - 1] ? monthlyGoalProjection?.[monthlyGoalProjection?.length - 1]?.balance_nominal_end : "0",
    { format: true, digits: 4 },
  );

  const fmtPortfolioAmount = activeGoal && activeGoal?.portfolio_amount !== undefined ? separateNumber(activeGoal?.portfolio_amount || "0", { format: true, digits: 4 }) : "0";
  const fmtMonthlyInduction =
    activeGoal && activeGoal?.monthly_induction_nominal !== undefined ? separateNumber(activeGoal?.monthly_induction_nominal || "0", { format: true, digits: 4 }) : "0";
  const fmtActiveTargetNominal =
    activeGoal && activeGoal?.target_amount_nominal !== undefined ? separateNumber(activeGoal?.target_amount_nominal || "0", { format: true, digits: 4 }) : "0";

  const targetDateArgs = goalDuration ? dayjs().add(goalDuration, "years").utc().toISOString() : "";

  return (
    <div
      className='col-12 px-0'
      style={{
        borderRadius: "20px",
        // paddingTop: '4px'
        // marginTop: "12px",
        // backgroundColor: theme === "dark" ? "var(--theme-dark-one)" : "var(--theme-light-one)",
      }}
    >
      <div className='p-2 w-100 d-flex align-items-center justify-content-center'>
        <button
          className='btn gap-1 d-flex align-items-center justify-content-center'
          type='button'
          onClick={() => toggleGoalModal()}
          // data-bs-toggle="modal"
          // data-bs-target="#goalModal"
          style={{
            lineHeight: "1",
            fontSize: "0.82rem",
            padding: "7px 16px 7.5px 9px",
            // minWidth: '82px',
            borderRadius: "99px",
            ...categoryButton(theme),
          }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width={19}
            height={19}
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth={2}
            strokeLinecap='round'
            strokeLinejoin='round'
            className='icon icon-tabler icons-tabler-outline icon-tabler-plus'
          >
            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
            <path d='M12 5l0 14' />
            <path d='M5 12l14 0' />
          </svg>
          {t("modals.goal.button")}
        </button>
      </div>
      <div
        className={theme === "dark" ? "add-custom-scroll-dark" : "add-custom-scroll"}
        style={{
          maxHeight: screenSize >= 992 ? "calc(100dvh - 243px)" : screenSize >= 576 && screenSize <= 991 ? "calc(100dvh - 317px)" : "calc(100dvh - 264px)",
          minHeight: screenSize >= 992 ? "calc(100dvh - 243px)" : screenSize >= 576 && screenSize <= 991 ? "calc(100dvh - 317px)" : "calc(100dvh - 264px)",
          overflowY: "auto",
          scrollbarWidth: screenSize >= 576 ? "" : "none",
          borderRadius: "20px",
        }}
      >
        <div
          className='goals-container goal-container w-100 mx-auto px-3 pt-2 pb-3'
          style={{
            display: "grid",
            gridTemplateColumns: screenSize <= 576 ? "1fr" : screenSize >= 576 && screenSize <= 991 ? "1fr 1fr" : "1fr 1fr 1fr",
            gridTemplateRows: "1fr",
            gridAutoRows: "1fr",
            gap: "12px 12px",
          }}
        >
          {loadingGoals ? (
            <div
              className='w-100 position-absolute start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
              style={{
                color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
                top: "calc(50%)",
              }}
            >
              <div
                className=''
                role='status'
                style={{
                  fontSize: "1.2rem",
                  fontWeight: "300",
                }}
              >
                {t("app.loading.goals")}
              </div>
              <div
                className='spinner-border'
                style={{
                  fontSize: "0.7rem",
                  height: "24px",
                  width: "24px",
                  color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                }}
              >
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : !loadingGoals && (!goals || goals?.length === 0) ? (
            <div
              className='w-100 position-absolute start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
              style={{
                color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
                top: "calc(50%)",
              }}
            >
              <div
                className=''
                role='status'
                style={{
                  fontSize: "1.2rem",
                  color: theme === "dark" ? "rgb(200,200,200)" : "rgb(200,200,200)",
                }}
              >
                No Goals Found
              </div>
            </div>
          ) : (
            goals?.map((goal, index) => {
              const cardFlipped = chartFlipView !== null ? chartFlipView === index : false;
              return (
                <GoalCard data={goal} onGoalSelect={handleGoalSelect} getPortfolioSize={getPortfolioSize} getPortfolioPortion={getPortfolioPortion} key={index} />
                // <div className='goal-card p-0' key={index} onClick={() => handleCardFlip(index)}>
                //   <div
                //     className='goal-card-front row w-100 mx-auto rounded-4 pt-1 px-0 pb-3'
                //     style={{
                //       lineHeight: "1",
                //       // rowGap: '0.45rem',
                //       // backgroundColor: 'rgba(255,255,255,0.7)',
                //       // backgroundColor: theme === "light" ? "rgba(255,255,255,0.7)" : "rgb(100, 100, 100)",
                //       backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                //       border: "1px solid",
                //       borderColor: "var(--theme-card-border)",
                //       boxShadow: "rgba(99, 99, 99, 0.15) 0px 2px 8px 2px",
                //       borderRadius: "20px",
                //       minHeight: "110px",
                //       color: theme === 'dark' ? "rgba(219, 219, 219, 1)" : ""
                //       // transform: chartFlipView === index ? "rotateY(180deg)" : "",
                //     }}
                //   >
                //     <div className='col-12 p-3' style={{}}>
                //       {/* <div className="" style={{
                // 						fontSize: '0.9rem',
                // 						color: 'rgba(0,0,0,0.45)'
                // 					}}>
                // 						Budget Title
                // 					</div> */}
                //       <div
                //         className='d-flex align-items-center justify-content-center pe-3'
                //         style={{
                //           fontSize: "1.4rem",
                //         }}
                //       >
                //         <span className="goal-icon m-0">
                //           {goalIcon ? goalIcon : ''}
                //         </span>
                //         {/* <span className="me-2" style={{ fontSize: '1.2rem', color: 'var(--theme-light-primary)' }}>Rs</span> */}
                //         <span className="ms-2">{goal.goal_category}</span>
                //       </div>
                //     </div>

                //     <div className='col-12 p-0'>
                //       <hr className='m-0 mb-2' />
                //     </div>

                //     <div className='col-6 p-3 py-2' style={{}}>
                //       <div
                //         className=''
                //         style={{
                //           fontSize: "0.9rem",
                //           color: theme === 'dark' ? "rgba(160, 160, 160, 1)" : "rgba(0,0,0,0.45)",
                //         }}
                //       >
                //         Target Amount
                //       </div>
                //       <div
                //         className='mt-1'
                //         style={{
                //           fontSize: "1.4rem",
                //           letterSpacing: '.03em'
                //         }}
                //       >
                //         <span className='me-1' style={{ fontSize: "1rem", color: theme === "light" ? "var(--theme-light-primary)" : "rgb(177, 110, 180)" }}>
                //           Rs
                //         </span>
                //         {numberWithCommas(Math.round(parseFloat(goal.target_amount_nominal)))}
                //       </div>
                //     </div>
                //     <div className='col-6 p-3 py-2 mb-2' style={{}}>
                //       <div
                //         className=''
                //         style={{
                //           fontSize: "0.85rem",
                //           color: theme === 'dark' ? "rgba(160, 160, 160, 1)" : "rgba(0,0,0,0.45)",
                //         }}
                //       >
                //         Target Period
                //       </div>
                //       <div
                //         className='mt-1'
                //         style={{
                //           fontSize: "1.4rem",
                //         }}
                //       >
                //         {/* <span className="me-2" style={{ fontSize: '1.2rem', color: 'var(--theme-light-primary)' }}>Rs</span> */}
                //         {goal.goal_duration}
                //         <span
                //           className=''
                //           style={{
                //             fontSize: "1rem",
                //             paddingLeft: "0.1rem",
                //             color: theme === "light" ? "var(--theme-light-primary)" : "rgb(177, 110, 180)",
                //           }}
                //         >
                //           yrs
                //         </span>
                //       </div>
                //     </div>
                //     <div className='col-6 p-3 py-2 mb-3' style={{}}>
                //       <div
                //         className=''
                //         style={{
                //           fontSize: "0.85rem",
                //           color: theme === 'dark' ? "rgba(160, 160, 160, 1)" : "rgba(0,0,0,0.45)",
                //         }}
                //       >
                //         Monthly Induction
                //       </div>
                //       <div
                //         className='mt-1'
                //         style={{
                //           fontSize: "1.4rem",
                //           letterSpacing: '.03em'
                //         }}
                //       >
                //         <span className='me-1' style={{ fontSize: "1rem", color: theme === "light" ? "var(--theme-light-primary)" : "rgb(177, 110, 180)" }}>
                //           Rs
                //         </span>
                //         {numberWithCommas(Math.round(parseFloat(goal.monthly_induction_nominal)))}
                //       </div>
                //     </div>
                //     <div className='col-6 p-3 py-2 mb-2' style={{}}>
                //       <div
                //         className=''
                //         style={{
                //           fontSize: "0.85rem",
                //           color: theme === 'dark' ? "rgba(160, 160, 160, 1)" : "rgba(0,0,0,0.45)",
                //         }}
                //       >
                //         Target Date
                //       </div>
                //       <div
                //         className='mt-1'
                //         style={{
                //           fontSize: "1.4rem",
                //         }}
                //       >
                //         {/* <span className="me-2" style={{ fontSize: '1.2rem', color: 'var(--theme-light-primary)' }}>Rs</span> */}
                //         {dayjs(goal.target_date).format('DD MMM, YYYY')}
                //         {/* <span
                //           className=''
                //           style={{
                //             fontSize: "1rem",
                //             paddingLeft: "0.1rem",
                //             color: theme === "light" ? "var(--theme-light-primary)" : "rgb(177, 110, 180)",
                //           }}
                //         >
                //           yrs
                //         </span> */}
                //       </div>
                //     </div>
                //     {/* <div className='col-12 p-3 py-2 mt-1'>
                //       <div
                //         className=''
                //         style={{
                //           fontSize: "0.9rem",
                //           color: theme === 'dark' ? "rgba(160, 160, 160, 1)" : "rgba(0,0,0,0.45)",
                //         }}
                //       >
                //         Portfolios
                //       </div>
                //       <div
                //         className='row w-100 mx-auto mt-2'
                //         style={{
                //           fontSize: "1.4rem",
                //           minHeight: "65.5px",
                //           columnGap: "6px",
                //           rowGap: "4px",
                //         }}
                //       >
                //         {goal.portfolios_included?.map((item, idx) => {
                //           // const themeBasedButton = theme === 'light' ? {
                //           // 	["--bs-btn-color"]: "var(--theme-light-primary)",
                //           // 	["--bs-btn-bg"]: "rgb(248, 248, 248)",
                //           // 	["--bs-btn-border-color"]: "rgb(195 209 226)",
                //           // 	["--bs-btn-hover-color"]: "rgba(255,255,255,0.9)",
                //           // 	["--bs-btn-hover-bg"]: "var(--theme-light-primary)",
                //           // 	["--bs-btn-hover-border-color"]: "var(--theme-light-primary)",
                //           // 	["--bs-btn-active-bg"]: "rgb(87 147 221)",
                //           // 	["--bs-btn-active-border-color"]: "rgb(87 147 221)",
                //           // } : {
                //           // 	["--bs-btn-color"]:"rgb(222, 134, 228)",
                //           // 	["--bs-btn-bg"]:"rgb(70, 70, 70)",
                //           // 	["--bs-btn-border-color"]:"rgb(70, 70, 70)",
                //           // 	["--bs-btn-hover-color"]: "rgba(255,255,255,0.9)",
                //           // 	["--bs-btn-hover-bg"]:"rgb(182, 111, 186)",
                //           // 	["--bs-btn-hover-border-color"]:"rgb(182, 111, 186)",
                //           // 	["--bs-btn-active-bg"]:"rgb(160, 97, 163)",
                //           // 	["--bs-btn-active-border-color"]:"rgb(182, 111, 186)",
                //           // }
                //           return (
                //             <div className='col-auto p-0' key={idx}>
                //               <button
                //                 className='btn w-100 d-flex align-items-center justify-content-start gap-2 p-2 py-1 px-3 rounded-pill'
                //                 onClick={(e) => {
                //                   e.stopPropagation();
                //                 }}
                //                 type='button'
                //                 style={{
                //                   ...customPortfolioButton,
                //                   // ...themeBasedButton,
                //                   ...themeBasedPortfolioBtn(theme),
                //                   fontSize: "0.8rem",
                //                 }}
                //               >
                //                 {item.name}
                //               </button>
                //             </div>
                //           );
                //         })}
                //       </div>
                //     </div> */}
                //     <div className='col-12 px-3 mt-2'>
                //       <div
                //         className='progress'
                //         role='progressbar'
                //         aria-label='Basic example'
                //         aria-valuenow='75'
                //         aria-valuemin='0'
                //         aria-valuemax='100'
                //         style={{
                //           height: "0.55rem",
                //           backgroundColor: theme === "light" ? "rgb(220, 223, 226)" : "rgb(138, 138, 138)",
                //         }}
                //       >
                //         <div
                //           className='progress-bar'
                //           style={{
                //             backgroundColor: theme === "light" ? "var(--theme-light-primary)" : "rgb(199, 119, 204)",
                //             width: `${progress[index]}%`,
                //           }}
                //         ></div>
                //       </div>
                //       <div
                //         className='mt-1 text-center'
                //         style={{
                //           color: theme === "light" ? "rgb(168 182 199)" : "rgb(153, 93, 157)",
                //         }}
                //       >
                //         {`( ${progress[index]}% completed )`}
                //       </div>
                //     </div>
                //     {/* <div className="col-12 p-3 pt-2">
                // 					<div className="" style={{
                // 						fontSize: '0.85rem',
                // 						color: 'rgba(0,0,0,0.45)'
                // 					}}>
                // 						Status
                // 					</div>
                // 					<div className="row w-100 mx-auto flex-nowrap p-1 mt-2 px-2 rounded-pill" style={{
                // 						backgroundColor: 'rgb(217, 220, 227)',
                // 						gap: '6px'
                // 					}}>
                // 						<div className="col-1 p-0" style={{
                // 							height: '10px',
                // 							backgroundColor: 'rgb(196, 38, 38)',
                // 							borderRadius: '999px',
                // 							width: 'calc(20% - 4px)'
                // 						}}>

                // 						</div>
                // 						<div className="col-1 p-0" style={{
                // 							height: '10px',
                // 							backgroundColor: 'rgb(76, 76, 227)',
                // 							borderRadius: '999px',
                // 							width: 'calc(40% - 4px)'
                // 						}}>

                // 						</div>
                // 						<div className="col-1 p-0" style={{
                // 							height: '10px',
                // 							backgroundColor: 'rgb(227, 146, 0)',
                // 							borderRadius: '999px',
                // 							width: 'calc(40% - 4px)'
                // 						}}>

                // 						</div>
                // 					</div>
                // 				</div> */}
                //     {/* <div className="col-12 p-0">
                // 					<LineChart options={lineChartOptions} theme="light" />
                // 				</div> */}
                //   </div>
                //   {/* <div
                //     className='goal-card-back rounded-4'
                //     style={{
                //       backgroundColor: theme === "light" ? "rgb(229 239 251)" : "rgb(97, 106, 116)",
                //       boxShadow: `${theme === "light" ? "rgb(209 222 239)" : "rgb(141 152 168)"} 0px 2px 9px 2px`,
                //       border: `1px solid ${theme === "light" ? "rgb(178 194 255)" : "rgb(140 151 191)"}`,
                //       // opacity: chartFlipView === index ? "1" : "",
                //       // transform: chartFlipView === index ? "rotateY(0deg)" : "",
                //       pointerEvents: cardFlipped ? "" : "none",
                //     }}
                //   >
                //     <LineChart options={lineChartOptions} theme='light' />
                //   </div> */}
                // </div>
              );
            })
          )}
        </div>

        {paginationData && paginationData?.totalPages > 1 ? (
          <Pagination totalPages={paginationData?.totalPages} activePageNumber={activePageNumber} onChange={setActivePageNumber} theme={theme} />
        ) : (
          ""
        )}
      </div>

      {/* -- ADD GOAL -- */}
      <div className='modal fade' id='goalModal' tabIndex='-1' aria-labelledby='goalModal' aria-hidden='true' ref={goalModalRef}>
        <div
          className='modal-dialog modal-dialog-centered m-2 mx-md-auto'
          style={{
            ["--bs-modal-width"]: "740px",
          }}
        >
          <form
            className='modal-content'
            onSubmit={handleAddGoal}
            style={{
              backgroundColor: theme === "light" ? "rgb(245, 246, 250)" : "rgba(36, 40, 49, 1)",
              color: theme === "light" ? "rgb(0,0,0)" : "rgb(232, 232, 232)",
            }}
          >
            <div className='modal-header align-items-center justify-content-between border-0'>
              <div style={{ height: "32px", width: "32px", marginLeft: ".15rem" }}></div>

              <h1 className='modal-title fs-4' id='goalModal'>
                {t("modals.goal.title")}
              </h1>
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  ["--bs-btn-close-bg"]: `url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27 fill=%27%23${theme === "light" ? "000" : "fff"}%27%3e%3cpath d=%27M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z%27/%3e%3c/svg%3e")`,
                }}
                onClick={() => toggleGoalModal()}
              ></button>
            </div>

            <hr className='m-0 mx-3' />

            <div className='modal-body px-5'>
              <div className='row g-3 mb-4'>
                <div className='col-md-4'>
                  <div className={`goal-category${goalData.category === "CAR" ? " selected" : ""}`} onClick={() => setGoalData((prev) => ({ ...prev, category: "CAR" }))}>
                    <div className='goal-icon'>
                      <CarIcon width={40} height={40} />
                    </div>
                    <div>{t("goals.modal.category.car")}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className={`goal-category${goalData.category === "MOBILE" ? " selected" : ""}`} onClick={() => setGoalData((prev) => ({ ...prev, category: "MOBILE" }))}>
                    <div className='goal-icon'>
                      <MobileIcon width={40} height={40} />
                    </div>
                    <div>{t("goals.modal.category.mobile")}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className={`goal-category${goalData.category === "PROPERTY" ? " selected" : ""}`} onClick={() => setGoalData((prev) => ({ ...prev, category: "PROPERTY" }))}>
                    <div className='goal-icon'>
                      <PropertyIcon width={40} height={40} />
                    </div>
                    <div>{t("goals.modal.category.property")}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div
                    className={`goal-category${goalData.category === "EDUCATION" ? " selected" : ""}`}
                    onClick={() => setGoalData((prev) => ({ ...prev, category: "EDUCATION" }))}
                  >
                    <div className='goal-icon'>
                      <EducationIcon width={40} height={40} />
                    </div>
                    <div>{t("goals.modal.category.education")}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className={`goal-category${goalData.category === "MARRIAGE" ? " selected" : ""}`} onClick={() => setGoalData((prev) => ({ ...prev, category: "MARRIAGE" }))}>
                    <div className='goal-icon'>
                      <MarriageIcon width={40} height={40} />
                    </div>
                    <div>{t("goals.modal.category.marriage")}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div
                    className={`goal-category${goalData.category === "RETIREMENT" ? " selected" : ""}`}
                    onClick={() => setGoalData((prev) => ({ ...prev, category: "RETIREMENT" }))}
                  >
                    <div className='goal-icon'>
                      <RetirementIcon width={40} height={40} />
                    </div>
                    <div>{t("goals.modal.category.retirement")}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className={`goal-category${goalData.category === "TRAVEL" ? " selected" : ""}`} onClick={() => setGoalData((prev) => ({ ...prev, category: "TRAVEL" }))}>
                    <div className='goal-icon'>
                      <TravelIcon width={40} height={40} />
                    </div>
                    <div>{t("goals.modal.category.travel")}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className={`goal-category${goalData.category === "BUSINESS" ? " selected" : ""}`} onClick={() => setGoalData((prev) => ({ ...prev, category: "BUSINESS" }))}>
                    <div className='goal-icon'>
                      <BusinessIcon width={40} height={40} />
                    </div>
                    <div>{t("goals.modal.category.business")}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className={`goal-category${goalData.category === "OTHER" ? " selected" : ""}`} onClick={() => setGoalData((prev) => ({ ...prev, category: "OTHER" }))}>
                    <div className='goal-icon'>
                      <OtherIcon width={40} height={40} />
                    </div>
                    <div>{t("goals.modal.category.other")}</div>
                  </div>
                </div>
              </div>

              <div className='mb-4'>
                <h3 className='section-title'>{t("goals.card.amount")}</h3>
                {/* <label className="form-label">Target Amount</label> */}
                <div
                  className='w-100 d-flex align-items-center flex-nowrap overflow-auto gap-2 px-3 mb-2'
                  style={{
                    scrollbarWidth: "none",
                  }}
                >
                  {["50000", "100000", "500000", "1000000", "5000000"].map((num, idx) => {
                    return (
                      <button
                        className={`btn${parseFloat(goalData?.target_amount) === parseFloat(num) ? " active" : ""}`}
                        value={num}
                        key={idx}
                        name='target_amount'
                        onClick={(e) => handleInputChange(e)}
                        type='button'
                        style={{
                          fontSize: "13px",
                          paddingBlock: "3px",
                          fontWeight: "300",
                          lineHeight: "1.25",
                          flex: "1",
                          borderRadius: "999px",
                          ...tabButton(theme),
                        }}
                      >
                        {numberWithCommas(num)}
                      </button>
                    );
                  })}
                </div>
                <div className='currency-input'>
                  <span className='currency-label'>Rs.</span>
                  <input
                    type='text'
                    className='form-control '
                    name='target_amount'
                    value={goalData.target_amount && goalData.target_amount.length !== 0 ? numberWithCommas(goalData.target_amount) : goalData.target_amount}
                    onChange={handleInputChange}
                    placeholder=''
                    id='targetAmount'
                  />
                  {/* <span className="currency-description">Fifty Millions</span> */}
                </div>
                <div
                  className='text-center'
                  style={{
                    color: theme === "dark" ? "#6c757d" : "",
                    fontSize: ".9rem",
                    marginTop: "4px",
                  }}
                >
                  {goalData.target_amount ? amountToWords(goalData.target_amount) : ""}
                </div>
              </div>

              <div className='mb-4'>
                <h3 className='section-title'>{t("goals.modal.select")}</h3>
                <div className='row g-3'>
                  <div className='col-md-6'>
                    <div
                      className={`portfolio-card p-4 card h-100 d-block${goalData.portfolio.id === 1 ? " selected" : ""}`}
                      onClick={() => setGoalData((prev) => ({ ...prev, portfolio: { id: 1 } }))}
                    >
                      <div className='text-center d-flex flex-column flex-nowrap justify-content-between'>
                        {/* <i className="bi bi-shield-check text-success" style={{ fontSize: "2.5rem" }}></i> */}
                        <h6 className='mb-4'>My Portfolio</h6>
                        <div className='row'>
                          <div className='col-6'>
                            <div className='portfolio-metric text-start'>{t("goals.common.size")}</div>
                            <div className='portfolio-value text-start'>{numberWithCommas(unitholderPortfolioSize)}</div>
                          </div>
                          <div className='col-6'>
                            <div className='portfolio-metric text-end'>{t("goals.card.growth")}:</div>
                            <div className='portfolio-value text-end'>{goalData.portfolioGrowthRate}%</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className='col-md-6'>
                    <div
                      className={`portfolio-card p-4 card h-100 d-block${goalData.portfolio.id === 2 ? " selected" : ""}`}
                      onClick={() => setGoalData((prev) => ({ ...prev, portfolio: { id: 2 } }))}
                    >
                      <div className='text-center d-flex flex-column flex-nowrap justify-content-between'>
                        <h6 className='mb-4'>My Portfolio</h6>
                        <div className='row'>
                          <div className='col-6'>
                            <div className='portfolio-metric text-start'>Size:</div>
                            <div className='portfolio-value text-start'>1 million</div>
                          </div>
                          <div className='col-6'>
                            <div className='portfolio-metric text-end'>Growth Rate:</div>
                            <div className='portfolio-value text-end'>12%</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> */}
                </div>

                <div className='mt-4'>
                  <h6 className='section-subtitle mb-4'>{t("goals.modal.portion")}</h6>
                  <div className='slider-container'>
                    <SingleRange
                      min={0}
                      max={100}
                      initial={goalData.portfolioProportion}
                      value={goalData.portfolioProportion && goalData.portfolioProportion !== 0 ? goalData.portfolioProportion : 0}
                      onChange={handlePortfolioPortionChange}
                    />
                  </div>
                </div>

                <div className='row g-3 mt-2'>
                  <div className='col-md-6'>
                    <label className='form-label'>{t("goals.common.amount")}</label>
                    <div className='currency-input'>
                      <span className='currency-label'>Rs</span>
                      <input
                        type='text'
                        className='form-control text-start'
                        value={goalData.presentValue && goalData.presentValue.length !== 0 ? numberWithCommas(goalData.presentValue) : ""}
                        disabled={true}
                        placeholder=''
                        name='inflation_rate'
                        id='inflationRate'
                        style={{
                          // width: "50px",
                          // maxWidth: "50px",
                          paddingInline: "50px 15px",
                          paddingBlock: "10px",
                          fontSize: "18px",
                        }}
                      />
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label'>{t("goals.card.growth")}</label>
                    <div className='currency-input'>
                      <span className='currency-label'>%</span>
                      <input
                        type='number'
                        className='form-control text-start'
                        value={goalData.portfolioGrowthRate}
                        onChange={handleOptionsChange}
                        placeholder=''
                        name='growth_rate'
                        id='inflationRate'
                        style={{
                          // width: "120px",
                          // maxWidth: "120px",
                          // paddingInline: "15px",
                          paddingInline: "50px 15px",
                          paddingBlock: "10px",
                          fontSize: "18px",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className='mb-4'>
                <h3 className='section-title'>{t("goals.modal.target")}</h3>
                <p className='mb-4' style={{ color: theme === "dark" ? "#b2cbdb" : "" }}>
                  {t("goals.modal.message")}
                </p>

                <div className='mb-5'>
                  <h6 className='section-subtitle' style={{ marginBottom: "36px" }}>
                    {t("goals.modal.age")}
                  </h6>
                  <div className='slider-container'>
                    <MultiRange min={0} max={100} lockedValue={parseFloat(unitholderAge || "20") || 20} initial={goalData.targetAge} value={goalData.targetAge} onChange={handleGoalDurationChange} />
                  </div>
                  <div className='target-info'>
                    <div className='row text-center'>
                      <div className='col-md-4'>
                        <div style={{ color: theme === "dark" ? "#a19d9dff" : "" }}>
                          {t("goals.common.age")}{" "}
                          <span id='currentAge' style={{ color: theme === "dark" ? "rgba(226, 145, 231, 1)" : "" }}>
                            {unitholderAge} {t("goals.common.year")}
                          </span>
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div style={{ color: theme === "dark" ? "#a19d9dff" : "" }}>
                          {t("goals.modal.duration")}:{" "}
                          <span id='goalDuration' style={{ color: theme === "dark" ? "rgba(226, 145, 231, 1)" : "" }}>
                            {goalDuration} {t("goals.common.year")}
                          </span>{" "}
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div style={{ color: theme === "dark" ? "#a19d9dff" : "" }}>
                          {t("goals.card.date")}:{" "}
                          <span id='targetDate' style={{ color: theme === "dark" ? "rgba(226, 145, 231, 1)" : "" }}>
                            {dayjs().add(goalDuration, "years").format("DD MMM YYYY")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='mb-4'>
                  <div className='d-flex align-items-center justify-content-between' style={{ marginBottom: "12px" }}>
                    <h3 className='section-title mb-0 d-inline-block'>{t("goals.modal.inflation.amount")}</h3>
                    <div className='d-flex align-items-center flex-nowrap'>
                      <span className='section-title mb-0' style={{ fontSize: "18px", color: theme === "dark" ? "rgb(149, 147, 150)" : "" }}>
                        {t("goals.modal.inflation.rate")}{" "}
                      </span>
                      <span className='ms-2'>
                        <div className='currency-input d-inline-block'>
                          {/* <span className='currency-label'>%</span> */}
                          <input
                            type='number'
                            className='form-control'
                            value={goalData.inflationRate}
                            onChange={handleOptionsChange}
                            placeholder=''
                            name='inflation_rate'
                            id='inflationRate'
                            style={{
                              width: "60px",
                              maxWidth: "60px",
                              paddingInline: "6px",
                              paddingBlock: "7px",
                              fontSize: "16px",
                            }}
                          />
                        </div>
                        <span className='ms-2' style={{ color: theme === "dark" ? "rgb(149, 147, 150)" : "" }}>
                          %
                        </span>
                      </span>
                    </div>
                  </div>
                  {/* <p className='' style={{ color: theme === "dark" ? "#b2cbdb" : "", marginBottom: '34px' }}>
                    Target amount with inflation rate of
                    <span style={{ marginInline: "8px", color: theme === "dark" ? "var(--theme-light-secondary)" : "", fontSize: "18px" }}>
                      <div className='currency-input d-inline-block'>
                              <input
                                type='text d-inline-block'
                                className='form-control'
                                value={goalData.inflationRate}
                                onChange={handleOptionsChange}
                                placeholder='Enter inflation rate'
                                name='inflation_rate'
                                id='inflationRate'
                                style={{
                                  width: '70px',
                                  maxWidth: '70px',
                                  paddingInline: '16px',
                                  paddingBlock: '12px'
                                }}
                              />
                            </div>
                            <span className="ms-2">%</span>
                    </span>
                    and
                    <span style={{ marginInline: "8px", color: theme === "dark" ? "var(--theme-light-secondary)" : "", fontSize: "18px" }}>
                      <div className='currency-input d-inline-block'>
                              <input
                                type='text d-inline-block'
                                className='form-control'
                                value={goalData.portfolioGrowthRate}
                                onChange={handleOptionsChange}
                                placeholder='Enter growth rate'
                                name='growth_rate'
                                id='inflationRate'
                                style={{
                                  width: '70px',
                                  maxWidth: '70px',
                                  paddingInline: '16px',
                                  paddingBlock: '12px'
                                }}
                              />
                            </div>
                            <span className="ms-2">%</span>
                    </span>
                    is
                  </p> */}
                  {/* <label className="form-label">Target Amount</label> */}
                  <div className='currency-input'>
                    <span className='currency-label'>Rs.</span>
                    <input
                      type='text'
                      className='form-control '
                      value={goalData.target_amount_nominal ? numberWithCommas(goalData.target_amount_nominal) : goalData.target_amount_nominal}
                      // onChange={handleInputChange}
                      name='monthly_induction'
                      placeholder=''
                      id='monthlyAmount'
                    />
                    {/* <span className="currency-description">Fifty Millions</span> */}
                  </div>
                  <div
                    className='text-center'
                    style={{
                      color: theme === "dark" ? "#6c757d" : "",
                      fontSize: ".9rem",
                      marginTop: "4px",
                    }}
                  >
                    {goalData.target_amount_nominal ? amountToWords(goalData.target_amount_nominal) : ""}
                  </div>
                </div>

                <hr className='mx-1' style={{ marginBlock: "28px", marginTop: "28px", color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />

                <div className='mb-4'>
                  <h3 className='section-title' style={{ marginBottom: "16px" }}>
                    {t("goals.modal.induction")}
                  </h3>
                  {/* <label className="form-label">Target Amount</label> */}
                  <div className='currency-input'>
                    <span className='currency-label'>Rs.</span>
                    <input
                      type='text'
                      className='form-control '
                      value={goalData.monthly_induction ? numberWithCommas(goalData.monthly_induction) : goalData.monthly_induction}
                      onChange={handleInputChange}
                      name='monthly_induction'
                      placeholder=''
                      id='monthlyAmount'
                      style={{
                        fontSize: "22px",
                        letterSpacing: ".04em",
                        color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                      }}
                    />
                    {/* <span className="currency-description">Fifty Millions</span> */}
                  </div>
                  <div
                    className='text-center'
                    style={{
                      color: theme === "dark" ? "#6c757d" : "",
                      fontSize: ".9rem",
                      marginTop: "4px",
                    }}
                  >
                    {goalData.monthly_induction ? amountToWords(goalData.monthly_induction) : ""}
                  </div>
                </div>

                {/* <div className='mb-4'>
                  <button
                    className='btn btn-link p-0 text-decoration-none d-flex align-items-center'
                    type='button'
                    onClick={toggleOptions}
                    aria-expanded={otherOptions}
                    aria-controls='otherOptions'
                  >
                    <span className='' style={{ fontSize: "1.3rem", color: theme === "dark" ? "var(--theme-light-secondary)" : "" }}>
                      Other Options
                    </span>
                  </button>

                  <div
                    className='other-options-wrapper mt-4'
                    style={{
                      maxHeight: otherOptions ? "300px" : "0",
                      opacity: otherOptions ? 1 : 0,
                      transform: otherOptions ? "translateY(0)" : "translateY(-10px)",
                    }}
                  >
                    <div className='card border-0' style={{ background: "transparent" }}>
                      <div className='card-body p-0'>
                        <div className='row g-3'>
                          <div className='col-md-6'>
                            <label className='form-label'>Inflation Rate (%)</label>
                            <div className='currency-input'>
                              <span className='currency-label'>%</span>
                              <input
                                type='text'
                                className='form-control'
                                value={goalData.inflationRate}
                                onChange={handleOptionsChange}
                                placeholder='Enter inflation rate'
                                name='inflation_rate'
                                id='inflationRate'
                              />
                              <span className='currency-description'>Per Year</span>
                            </div>
                          </div>
                          <div className='col-md-6'>
                            <label className='form-label'>Expected Growth Rate (%)</label>
                            <div className='currency-input'>
                              <span className='currency-label'>%</span>
                              <input
                                type='text'
                                className='form-control'
                                value={goalData.portfolioGrowthRate}
                                onChange={handleOptionsChange}
                                placeholder='Enter growth rate'
                                name='growth_rate'
                                id='growthRate'
                              />
                              <span className='currency-description'>Per Year</span>
                            </div>
                          </div>
                        </div>
                        <div className='mt-1'>
                          <small className='text-secondary'>
                            These advanced options allow you to customize inflation and growth assumptions for more accurate projections.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
              </div>

              <div className='text-center mb-1'>
                <button
                  type='button'
                  className='btn btn-primary goals-btn btn-md d-inline-flex align-items-center justify-content-center gap-2'
                  onClick={() => {
                    if (!goalData?.target_amount || !(parseFloat(goalData?.target_amount) > 0)) {
                      toast((t) => customToast("Target amount is empty", "error", t), {
                        duration: 3000,
                        removeDelay: 1000,
                        style: {
                          padding: "0.75rem",
                          margin: "0",
                          minWidth: "200px",
                          backgroundColor: "rgb(255,255,255)",
                        },
                      });

                      return;
                    }

                    if (!goalData?.monthly_induction || !(parseFloat(goalData?.monthly_induction) > 0)) {
                      toast((t) => customToast("Monthly induction amount is empty", "error", t), {
                        duration: 3000,
                        removeDelay: 1000,
                        style: {
                          padding: "0.75rem",
                          margin: "0",
                          minWidth: "200px",
                          backgroundColor: "rgb(255,255,255)",
                        },
                      });

                      return;
                    }

                    computeMonthlyProjection();

                    modalProgressRef.current = "continue";

                    if (goalModalRef.current) {
                      modalRef.current?.toggle();
                    }

                    if (projectionModalRef.current) {
                      projectionModalInstanceRef.current?.toggle();
                    }
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width={28}
                    height={28}
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth={1.25}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-clipboard-data'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2' />
                    <path d='M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z' />
                    <path d='M9 17v-4' />
                    <path d='M12 17v-1' />
                    <path d='M15 17v-2' />
                    <path d='M12 17v-1' />
                  </svg>
                    {t("goals.modal.details")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* -- GOAL PROJECTION -- */}
      <div className='modal fade' id='projectionModal' tabIndex='-1' aria-labelledby='projectionModal' aria-hidden='true' ref={projectionModalRef}>
        <div
          className='modal-dialog modal-dialog-centered m-2 mx-md-auto'
          style={{
            ["--bs-modal-width"]: "740px",
          }}
        >
          <form
            className='modal-content'
            onSubmit={handleAddGoal}
            style={{
              backgroundColor: theme === "light" ? "rgb(250, 250, 250)" : "rgba(36, 40, 49, 1)",
              color: theme === "light" ? "rgb(0,0,0)" : "rgb(232, 232, 232)",
            }}
          >
            <div className='modal-header align-items-center justify-content-between border-0'>
              <div style={{ height: "32px", width: "32px", marginLeft: ".15rem" }}></div>

              <h1 className='modal-title fs-4' id='goalModal'>
                {t("goals.projection.title.main")}
              </h1>
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  ["--bs-btn-close-bg"]: `url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27 fill=%27%23${theme === "light" ? "000" : "fff"}%27%3e%3cpath d=%27M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z%27/%3e%3c/svg%3e")`,
                }}
                onClick={() => {
                  if (projectionModalInstanceRef.current) {
                    projectionModalInstanceRef.current?.toggle();
                  }
                }}
              ></button>
            </div>

            <hr className='m-0 mx-3' />

            <div className='modal-body px-3'>
              <div className='row gx-3 gy-3'>
                <div className='col-12'>
                  <div className={`goal-details-card pb-2`}>
                    <div
                      style={{
                        fontSize: "18px",
                        color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                        letterSpacing: ".02em",
                        lineHeight: "1.1",
                        marginBottom: "18px",
                      }}
                    >
                      {t("goals.projection.title.goal")}
                    </div>
                    <div className='w-100'>
                      {chartData ? <ProjectionTargetChart xData={chartData.xData} projection={chartData.projection} theme={theme} target={chartData.target} /> : ""}
                    </div>
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className={`goal-details-card`}>
                    <div className='d-flex align-items-center justify-content-start flex-nowrap gap-2 mb-4'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width={24}
                        height={24}
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth={1.5}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-antenna-bars-5'
                        style={{
                          color: theme === "dark" ? "#b2cbdb" : "",
                        }}
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M6 18l0 -3' />
                        <path d='M10 18l0 -6' />
                        <path d='M14 18l0 -9' />
                        <path d='M18 18l0 -12' />
                      </svg>
                      <div
                        style={{
                          fontSize: "18px",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          letterSpacing: ".02em",
                          lineHeight: "1.1",
                        }}
                      >
                        {t("goals.projection.title.input")}
                      </div>
                    </div>
                    <div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          {t("goals.projection.cards.portfolio")}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {goalData.presentValue && goalData.presentValue.length !== 0 ? numberWithCommas(fmtContribution.whole) : ""}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtContribution.decimal}</span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          {t("goals.card.induction")}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {goalData.monthly_induction_nominal && goalData.monthly_induction_nominal.length !== 0 ? numberWithCommas(fmtPayment.whole) : ""}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtPayment.decimal}</span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                         {t("goals.projection.cards.return")}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          {goalData.portfolioGrowthRate && goalData.portfolioGrowthRate.length !== 0 ? goalData.portfolioGrowthRate : ""}
                          <span className='ms-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            %
                          </span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          {t("goals.projection.cards.inflation")}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          {goalData.inflationRate !== null ? goalData.inflationRate : ""}
                          <span className='ms-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            %
                          </span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          {t("goals.projection.cards.duration")}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          {goalDuration && goalDuration.length !== 0 ? numberWithCommas(goalDuration) : ""}
                          <span className='ms-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            years
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className={`goal-details-card`}>
                    <div className='d-flex align-items-center justify-content-start flex-nowrap gap-2 mb-4'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width={24}
                        height={24}
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth={1.5}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-playstation-circle'
                        style={{
                          color: theme === "dark" ? "#b2cbdb" : "",
                        }}
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M12 21a9 9 0 0 0 9 -9a9 9 0 0 0 -9 -9a9 9 0 0 0 -9 9a9 9 0 0 0 9 9z' />
                        <path d='M12 12m-4.5 0a4.5 4.5 0 1 0 9 0a4.5 4.5 0 1 0 -9 0' />
                      </svg>
                      <div
                        style={{
                          fontSize: "18px",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          letterSpacing: ".02em",
                          lineHeight: "1.1",
                        }}
                      >
                        {t("goals.projection.title.projection")}
                      </div>
                    </div>
                    <div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          {t("goals.projection.cards.target")}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {goalData.target_amount && goalData.target_amount.length !== 0 ? numberWithCommas(goalData.target_amount) : ""}
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          {t("goals.projection.cards.targetInfation")}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {monthlyGoalProjection?.[monthlyGoalProjection?.length - 1] ? numberWithCommas(fmtFinalBalance.whole) : ""}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtFinalBalance.decimal}</span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          {t("goals.projection.cards.max")}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {goalData.target_amount_nominal && goalData.target_amount_nominal.length !== 0 ? numberWithCommas(fmtTargetNominal.whole) : ""}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtTargetNominal.decimal}</span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          {t("goals.projection.cards.nominal")}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {monthlyGoalProjection?.[monthlyGoalProjection?.length - 1] ? numberWithCommas(fmtFinalBalance.whole) : ""}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtFinalBalance.decimal}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-12'>
                  <div className='table-responsive' style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <table className='table table-dark goals-projection-table'>
                      <thead style={{ position: "sticky", top: "0", zIndex: "10" }}>
                        <tr>
                          <th>{t("goals.common.month")}</th>
                          <th>{t("goals.projection.table.age")}</th>
                          <th>
                            {t("goals.projection.table.month")}
                            <br />
                            {t("goals.projection.table.today")}
                          </th>
                          <th>
                            {t("goals.projection.table.realEnd")}
                            <br />
                            {t("goals.projection.table.realRupee")}
                          </th>
                          <th>
                            {t("goals.projection.table.realEnd")}
                            <br />
                            {t("goals.projection.table.nomRupee")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyGoalProjection?.map((projection) => {
                          const payment = separateNumber(projection.payment_nominal_equivalent, { format: true, digits: 4 });
                          const real_balance = separateNumber(projection.balance_real_end, { format: true, digits: 4 });
                          const nominal_balance = separateNumber(projection.balance_nominal_end, { format: true, digits: 4 });

                          return (
                            <tr key={projection.month}>
                              <td>{projection.month}</td>
                              <td>{projection.age}</td>
                              <td>
                                {numberWithCommas(payment.whole) || 0}
                                {"."}
                                <span style={{ fontSize: "13px" }}>{payment.decimal}</span>
                              </td>
                              <td>
                                {numberWithCommas(real_balance.whole) || 0}
                                {"."}
                                <span style={{ fontSize: "13px" }}>{real_balance.decimal}</span>
                              </td>
                              <td>
                                {numberWithCommas(nominal_balance.whole) || 0}
                                {"."}
                                <span style={{ fontSize: "13px" }}>{nominal_balance.decimal}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className='modal-footer border-0 justify-content-center justify-content-sm-center'>
              <div className='text-center mb-1'>
                <button
                  type='button'
                  className='btn btn-primary goals-btn btn-md d-inline-flex align-items-center justify-content-center gap-2'
                  onClick={() => {
                    const apiArgs = {
                      folio_no: user?.data?.folio_no || user?.data?.main_folio_no,
                      email: user?.data?.email || unitholder?.folio_no || unitholder?.main_folio_no,
                      portfolio_portion: String(goalData?.portfolioProportion / 100),
                      portfolio_growth_rate: String(goalData?.portfolioGrowthRate / 100),
                      portfolio_amount: goalData?.presentValue,
                      annual_inflation_rate: String(goalData?.inflationRate / 100),
                      goal_duration: goalDuration,
                      target_amount_real: goalData?.target_amount,
                      target_amount_nominal: goalData?.target_amount_nominal,
                      goal_category: goalData?.category,
                      portfolios_inlcuded: "[]",
                      monthly_induction_nominal: goalData?.monthly_induction_nominal,
                      monthly_induction_real: goalData?.monthly_induction_real,
                      target_date: targetDateArgs,
                      investor_bank_name: unitholder?.bank_name,
                      investor_branch_name: unitholder?.branch_name,
                      investor_bank_iban: unitholder?.account_no,
                      investor_account_title: unitholder?.account_title,
                    };

                    if (goalData?.monthly_induction_nominal && parseFloat(goalData?.monthly_induction_nominal) > 0) {
                      createInvestorGoal(apiArgs).then((res) => {
                        if (!res.error) {
                          toast((t) => customToast("Added Goal Successfully", "success", t), {
                            duration: 3000,
                            removeDelay: 1000,
                            style: {
                              padding: "0.75rem",
                              margin: "0",
                              minWidth: "200px",
                              backgroundColor: "rgb(255,255,255)",
                            },
                          });
                          const apiArgs = {
                            email: user?.data?.email,
                            folioNo: user?.data?.folio_no || user?.data?.main_folio_no,
                            pageNumber: activePageNumber,
                            pageSize: 12,
                          };
                          getInvestorGoals(apiArgs).then((res) => {
                            if (!res.error) {
                              const { docs, ...pageInfo } = res?.data;
                              setGoals(docs || []);
                              setPaginationData(pageInfo);
                            } else {
                              setFetchingError(true);
                            }
                          });

                          closeAfterGoal.current = true;

                          if (projectionModalRef.current) {
                            projectionModalInstanceRef.current?.toggle();
                          }
                        } else {
                          toast((t) => customToast("Failed to Add Goal", "error", t), {
                            duration: 3000,
                            removeDelay: 1000,
                            style: {
                              padding: "0.75rem",
                              margin: "0",
                              minWidth: "200px",
                              backgroundColor: "rgb(255,255,255)",
                            },
                          });
                        }
                      });
                    } else {
                      toast((t) => customToast("Monthly induction amount should not be empty", "error", t), {
                        duration: 3000,
                        removeDelay: 1000,
                        style: {
                          padding: "0.75rem",
                          margin: "0",
                          minWidth: "200px",
                          backgroundColor: "rgb(255,255,255)",
                        },
                      });
                    }
                  }}
                >
                  Create
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* -- GOAL DETAILS -- */}
      <div className='modal fade' id='projectionModal' tabIndex='-1' aria-labelledby='projectionModal' aria-hidden='true' ref={goalDetailsRef}>
        <div
          className='modal-dialog modal-dialog-centered m-2 mx-md-auto'
          style={{
            ["--bs-modal-width"]: "740px",
          }}
        >
          <form
            className='modal-content'
            onSubmit={handleAddGoal}
            style={{
              backgroundColor: theme === "light" ? "rgb(250, 250, 250)" : "rgba(36, 40, 49, 1)",
              color: theme === "light" ? "rgb(0,0,0)" : "rgb(232, 232, 232)",
            }}
          >
            <div className='modal-header align-items-center justify-content-between border-0'>
              <div style={{ height: "32px", width: "32px", marginLeft: ".15rem" }}></div>

              <h1 className='modal-title fs-4' id='goalModal'>
                Goal Details
              </h1>
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  ["--bs-btn-close-bg"]: `url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27 fill=%27%23${theme === "light" ? "000" : "fff"}%27%3e%3cpath d=%27M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z%27/%3e%3c/svg%3e")`,
                }}
                onClick={() => {
                  if (goalDetailsRef.current) {
                    goalDetailsInstanceRef.current?.toggle();
                  }
                }}
              ></button>
            </div>

            <hr className='m-0 mx-3' />

            <div className='modal-body px-3'>
              <div className='row gx-3 gy-3'>
                <div className='col-12'>
                  <div className={`goal-details-card pb-2`}>
                    <div
                      style={{
                        fontSize: "18px",
                        color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                        letterSpacing: ".02em",
                        lineHeight: "1.1",
                        marginBottom: "18px",
                      }}
                    >
                      Goal Projection with Inflation
                    </div>
                    <div className='w-100'>
                      {chartData ? <ProjectionTargetChart xData={chartData.xData} projection={chartData.projection} theme={theme} target={chartData.target} /> : ""}
                    </div>
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className={`goal-details-card`}>
                    <div className='d-flex align-items-center justify-content-start flex-nowrap gap-2 mb-4'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width={24}
                        height={24}
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth={1.5}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-antenna-bars-5'
                        style={{
                          color: theme === "dark" ? "#b2cbdb" : "",
                        }}
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M6 18l0 -3' />
                        <path d='M10 18l0 -6' />
                        <path d='M14 18l0 -9' />
                        <path d='M18 18l0 -12' />
                      </svg>
                      <div
                        style={{
                          fontSize: "18px",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          letterSpacing: ".02em",
                          lineHeight: "1.1",
                        }}
                      >
                        Input Summary
                      </div>
                    </div>
                    <div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Portfolio Allocated
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {numberWithCommas(fmtPortfolioAmount?.whole)}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtPortfolioAmount?.decimal}</span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Monthly Induction
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {numberWithCommas(fmtMonthlyInduction?.whole)}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtMonthlyInduction?.decimal}</span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Expected Return
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          {activeGoal?.portfolio_growth_rate ? activeGoal?.portfolio_growth_rate * 100 : ""}
                          <span className='ms-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            %
                          </span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          {"Inflation (yearly)"}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          {activeGoal?.annual_inflation_rate !== undefined ? Math.round(activeGoal?.annual_inflation_rate * 100) : ""}
                          <span className='ms-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            %
                          </span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Duration
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          {activeGoal?.goal_duration ? numberWithCommas(activeGoal?.goal_duration) : ""}
                          <span className='ms-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            years
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className={`goal-details-card`}>
                    <div className='d-flex align-items-center justify-content-start flex-nowrap gap-2 mb-4'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width={24}
                        height={24}
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth={1.5}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-playstation-circle'
                        style={{
                          color: theme === "dark" ? "#b2cbdb" : "",
                        }}
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M12 21a9 9 0 0 0 9 -9a9 9 0 0 0 -9 -9a9 9 0 0 0 -9 9a9 9 0 0 0 9 9z' />
                        <path d='M12 12m-4.5 0a4.5 4.5 0 1 0 9 0a4.5 4.5 0 1 0 -9 0' />
                      </svg>
                      <div
                        style={{
                          fontSize: "18px",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          letterSpacing: ".02em",
                          lineHeight: "1.1",
                        }}
                      >
                        Projection Summary
                      </div>
                    </div>
                    <div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Target (today's rupees)
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {activeGoal?.target_amount_real ? numberWithCommas(activeGoal?.target_amount_real) : ""}
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Target with inflation
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {monthlyGoalProjection?.[monthlyGoalProjection?.length - 1] ? numberWithCommas(fmtFinalBalance.whole) : ""}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtFinalBalance.decimal}</span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Final Portfolio Value (max)
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {numberWithCommas(fmtActiveTargetNominal?.whole)}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtActiveTargetNominal?.decimal}</span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Final Portfolio Value (nomi)
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {monthlyGoalProjection?.[monthlyGoalProjection?.length - 1] ? numberWithCommas(fmtFinalBalance.whole) : ""}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtFinalBalance.decimal}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-12'>
                  <div className='table-responsive' style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <table className='table table-dark goals-projection-table'>
                      <thead style={{ position: "sticky", top: "0", zIndex: "10" }}>
                        <tr>
                          <th>Month</th>
                          <th>Age</th>
                          <th>
                            Monthly Payment
                            <br />
                            {"(Today Rs)"}
                          </th>
                          <th>
                            End Balance
                            <br />
                            {"(Real Rs)"}
                          </th>
                          <th>
                            End Balance
                            <br />
                            {"(Nominal Rs)"}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyGoalProjection?.map((projection) => {
                          const payment = separateNumber(projection.payment_nominal_equivalent, { format: true, digits: 4 });
                          const real_balance = separateNumber(projection.balance_real_end, { format: true, digits: 4 });
                          const nominal_balance = separateNumber(projection.balance_nominal_end, { format: true, digits: 4 });

                          return (
                            <tr key={projection.month}>
                              <td>{projection.month}</td>
                              <td>{projection.age}</td>
                              <td>
                                {numberWithCommas(payment.whole) || 0}
                                {"."}
                                <span style={{ fontSize: "13px" }}>{payment.decimal}</span>
                              </td>
                              <td>
                                {numberWithCommas(real_balance.whole) || 0}
                                {"."}
                                <span style={{ fontSize: "13px" }}>{real_balance.decimal}</span>
                              </td>
                              <td>
                                {numberWithCommas(nominal_balance.whole) || 0}
                                {"."}
                                <span style={{ fontSize: "13px" }}>{nominal_balance.decimal}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            {/* <div className='modal-footer border-0 justify-content-center justify-content-sm-center'>
              <div className='text-center mb-1'>
                <button
                  type='button'
                  className='btn btn-primary goals-btn btn-md d-inline-flex align-items-center justify-content-center gap-2'
                  onClick={() => {
                    const apiArgs = {
                      folio_no: user?.data?.folio_no || user?.data?.main_folio_no,
                      email: user?.data?.email || unitholder?.folio_no || unitholder?.main_folio_no,
                      portfolio_portion: String(goalData?.portfolioProportion / 100),
                      portfolio_growth_rate: String(goalData?.portfolioGrowthRate / 100),
                      portfolio_amount: goalData?.presentValue,
                      annual_inflation_rate: String(goalData?.inflationRate / 100),
                      goal_duration: goalDuration,
                      target_amount_real: goalData?.target_amount,
                      target_amount_nominal: goalData?.target_amount_nominal,
                      goal_category: goalData?.category,
                      portfolios_inlcuded: "[]",
                      monthly_induction_nominal: goalData?.monthly_induction_nominal,
                      monthly_induction_real: goalData?.monthly_induction_real,
                      target_date: targetDateArgs,
                      investor_bank_name: unitholder?.bank_name,
                      investor_branch_name: unitholder?.branch_name,
                      investor_bank_iban: unitholder?.account_no,
                      investor_account_title: unitholder?.account_title
                    }

                    if (goalData?.monthly_induction_nominal && parseFloat(goalData?.monthly_induction_nominal) > 0) {
                      createInvestorGoal(apiArgs).then(res => {
                        if (!res.error) {
                          toast((t) => customToast("Added Goal Successfully", "success", t), {
                            duration: 3000,
                            removeDelay: 1000,
                            style: {
                              padding: "0.75rem",
                              margin: "0",
                              minWidth: "200px",
                              backgroundColor: "rgb(255,255,255)",
                            },
                          });
                          const apiArgs = {
                            email: user?.data?.email,
                            folioNo: user?.data?.folio_no || user?.data?.main_folio_no,
                            pageNumber: 1
                          }
                          getInvestorGoals(apiArgs).then((res) => {
                            if (!res.error) {
                              setGoals(res.data?.docs || [])
                            } else {
                              setFetchingError(true)
                            }
                          })

                          closeAfterGoal.current = true;

                          if (projectionModalRef.current) {
                            projectionModalInstanceRef.current?.toggle();
                          }

                        } else {
                          toast((t) => customToast("Failed to Add Goal", "error", t), {
                            duration: 3000,
                            removeDelay: 1000,
                            style: {
                              padding: "0.75rem",
                              margin: "0",
                              minWidth: "200px",
                              backgroundColor: "rgb(255,255,255)",
                            },
                          });
                        }
                      })
                    } else {
                      toast((t) => customToast("Monthly induction amount should not be empty", "error", t), {
                        duration: 3000,
                        removeDelay: 1000,
                        style: {
                          padding: "0.75rem",
                          margin: "0",
                          minWidth: "200px",
                          backgroundColor: "rgb(255,255,255)",
                        },
                      });
                    }
                  }}
                >
                  Add Goal
                </button>
              </div>
            </div> */}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Goals;
